import * as React from "react"
import { useEffect, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
	Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent,
	Divider, Drawer, IconButton, InputAdornment, Link, MenuItem, Paper,
	Stack, TextField, Tooltip, Typography,
} from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'

import { BACKEND_APIS, VISIBILITY_TRACKER_ROUTES } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import {
	EditBrands, EditCategories, ProductsEditor,
	UnmatchedBrandChips, _resolveUnmatched,
	ensureBrandCatalogue,
} from "../Review"
import {
	retailers, sections, items, counts, total,
	selectedRetailerId, selectedSectionId, selectedCountry, selectedKind, selectedParentKinds,
	selectedFamily,
	selectedIntents, selectedCompositions, selectedCategories, selectedBrands,
	selectedNeedsQa, selectedIgnored, minCountSeen,
	dateFrom, dateTo, createdFrom, createdTo, freeText, freeTextQuery,
	brandFacets, brandNames, brandsExpanded, brandsUnresolved, brandFilter,
	page, limit,
	isLoading, detailResource, detailLoading,
	mergeMode, mergeSelection,
	clearResourcesSignals,
} from "./@signals"
import CallMergeIcon from '@mui/icons-material/CallMerge'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'


// ── Composition vocab + helpers (mirror the back's resource_match_svc
// rule, also duplicated in ReviewQueue). Keep in sync if either side
// changes. ────────────────────────────────────────────────────────────
const COMPOSITIONS_FRAGRANCE = [
	{ value: 'multicompany', label: 'Multicompany' },
	{ value: 'monocompany',  label: 'Monocompany'  },
	{ value: 'monobrand',    label: 'Monobrand'    },
	{ value: 'monoproduct',  label: 'Monoproduct'  },
	{ value: 'monoline',     label: 'Monoline'     },
	{ value: 'multiline',    label: 'Multiline'    },
	{ value: 'masterbrand',  label: 'Masterbrand'  },
]
const COMPOSITIONS_NON_FRAGRANCE = COMPOSITIONS_FRAGRANCE
	.filter(o => ['multicompany', 'monocompany', 'monobrand', 'monoproduct'].includes(o.value))

function isFragranceOnly(categories) {
	const cats = (categories || []).filter(Boolean)
	return cats.length === 1 && cats[0] === 'fragrances'
}
function allowedCompositions(categories) {
	return isFragranceOnly(categories) ? COMPOSITIONS_FRAGRANCE : COMPOSITIONS_NON_FRAGRANCE
}

// Closed `intent` vocab for the EDITOR — same as ReviewQueue / back
// resource_match_svc. Distinct from the filter palette `INTENTS`
// further down (which is `Object.keys(INTENT_PALETTE)`) — they happen
// to overlap, but the filter palette also drives chip colours and
// keeping the names apart avoids a redeclaration clash.
const EDITOR_INTENTS = ['promotion', 'news', 'brand', 'editorial', 'bestseller', 'category', 'other']
// `other` is not offered as a manual pick — same rule as the ReviewQueue
// editor, see the long note there for the numbers. It is the machine's
// sink (coerce_intent) and, as a chip, a duplicate of `editorial`; it
// stays on screen only while it IS the current value so the AI's pick is
// still legible. The filter palette below keeps it: 3327 rows carry it.
const EDITOR_INTENT_CHOICES = EDITOR_INTENTS.filter(i => i !== 'other')
function editorIntentChoices(current) {
	return current === 'other' ? [...EDITOR_INTENT_CHOICES, 'other'] : EDITOR_INTENT_CHOICES
}
const INTENT_KINDS = new Set(['banner', 'slider', 'interstitial'])

// Sanitiser mirror of the ReviewQueue helper — MUI Autocomplete chokes
// on non-dict `brands` entries (e.g. raw strings the AI normaliser
// hasn't resolved). Drop them so the input keeps working.
function _sanitiseBrands(raw) {
	if (!Array.isArray(raw)) return []
	const out = []
	for (const b of raw) {
		if (b && typeof b === 'object' && b.id_brand != null) out.push(b)
	}
	return out
}

// Same qa_reason parser the ReviewQueue uses to reconstruct
// `unmatched_brands` so UnmatchedBrandChips renders clickable chips
// from a resource that only carries `qa_reason` text.
function _parseUnmatchedFromQaReason(reason) {
	if (!reason || typeof reason !== 'string') return []
	const m = reason.match(/(?:unmatched brand\(s\)|brand catalog gap)\s*:\s*(.+?)\s*(?:[…]|\.\.\.|$)/i)
	if (!m) return []
	return m[1].split(',').map(s => s.trim()).filter(Boolean)
}


// Leaf kinds the matcher recognises. We pull them as a static list rather
// than from the back because (a) the back doesn't expose an enum, and (b)
// they're a closed set tied to the capture pipeline — if they change,
// this list is the single source of truth in the dashboard.
const LEAF_KINDS = ['banner', 'interstitial', 'slide', 'product-slide', 'logo', 'product']

// Identity families (R1). What a resource IS, as opposed to where it
// showed up — that lives on the occurrence now, because the same product
// appears in a product-list one day and in a product-slider the next.
//
// The kind → family mapping is deliberately NOT repeated here: the back
// already sends `family` / `family_label`, derived from the same table the
// matcher and the merges use (vt_identity_svc). A second copy would drift,
// and the dashboard would label something "media" that the matcher treats
// as a product, with nobody noticing.
const FAMILY_PALETTE = {
	product: { label: 'Product', bg: '#2e7d32', fg: '#fff' },  // green 800
	logo:    { label: 'Logo',    bg: '#ed6c02', fg: '#fff' },  // orange 800
	media:   { label: 'Media',   bg: '#1976d2', fg: '#fff' },  // blue 700
}
const FAMILIES = Object.keys(FAMILY_PALETTE)

// A resource whose sightings span more than one family breaks R1. That is
// a data error, not a state to render nicely, so it gets an alarm chip
// instead of being quietly filed under whichever family happens to win.
function familyStyle(f) {
	return FAMILY_PALETTE[f] || { label: 'MIXED', bg: '#b71c1c', fg: '#fff' }
}

// Parent-kind palette — single source of truth for both the filter chip
// row and the per-card kind chip, so a card visually echoes the chip the
// user clicked to filter it in. Each entry: { label (human-readable),
// bg (chip background), fg (chip text). Picked from MUI's Material
// palette ramps with enough chroma separation to be told apart at chip
// size; if you add a new parent_kind to the back, add it here too.
const PARENT_KIND_PALETTE = {
	'banner':         { label: 'Banner',         bg: '#1976d2', fg: '#fff' },  // blue 700
	'slider':         { label: 'Slider',         bg: '#7b1fa2', fg: '#fff' },  // purple 700
	'product-slider': { label: 'Product Slider', bg: '#2e7d32', fg: '#fff' },  // green 800
	'product-list':   { label: 'Product List',   bg: '#0288d1', fg: '#fff' },  // light-blue 700
	'brand-logos':    { label: 'Brand Logos',    bg: '#ed6c02', fg: '#fff' },  // orange 800
	'interstitial':   { label: 'Interstitial',   bg: '#c62828', fg: '#fff' },  // red 800
}
const PARENT_KINDS = Object.keys(PARENT_KIND_PALETTE)

function parentKindStyle(pk) {
	return PARENT_KIND_PALETTE[pk] || { label: pk || 'unknown', bg: '#757575', fg: '#fff' }
}


// Intent palette — what the creative is trying to communicate. Distinct
// hue family from PARENT_KIND_PALETTE (those use saturated MUI primaries;
// these use softer, more muted tones) so a card visibly carries TWO
// different chips — Media Type (parent_kind) and Intent — without the
// human confusing them. Mirrors `INTENTS` in
// resource_match_svc.py — keep in sync if the back set changes.
const INTENT_PALETTE = {
	promotion:  { label: 'Promotion',  bg: '#d81b60', fg: '#fff' },  // pink 600
	news:       { label: 'News',       bg: '#00838f', fg: '#fff' },  // cyan 800
	brand:      { label: 'Brand',      bg: '#5d4037', fg: '#fff' },  // brown 700
	editorial:  { label: 'Editorial',  bg: '#455a64', fg: '#fff' },  // blue-grey 700
	bestseller: { label: 'Bestseller', bg: '#f9a825', fg: '#212121' },  // yellow 800 — dark text
	category:   { label: 'Category',   bg: '#6a1b9a', fg: '#fff' },  // deep-purple 800
	other:      { label: 'Other',      bg: '#9e9e9e', fg: '#fff' },  // grey 500
}
const INTENTS = Object.keys(INTENT_PALETTE)

function intentStyle(it) {
	return INTENT_PALETTE[it] || null
}


// Composition palette — what mix of companies / brands / products the
// creative shows. Visually grouped: cool blues for multi-brand,
// neutral darks for monobrand-with-product-info, warmer for the
// subjective ones. Same single-source-of-truth pattern as the others;
// the card echoes the chip in the same colour so filtering by
// composition narrows the grid visibly. Keep aligned with
// resource_match_svc.COMPOSITIONS.
const COMPOSITION_PALETTE = {
	multicompany: { label: 'Multicompany', bg: '#0d47a1', fg: '#fff' },  // indigo 900
	monocompany:  { label: 'Monocompany',  bg: '#1976d2', fg: '#fff' },  // blue 700
	monobrand:    { label: 'Monobrand',    bg: '#37474f', fg: '#fff' },  // blue-grey 800
	monoproduct:  { label: 'Monoproduct',  bg: '#558b2f', fg: '#fff' },  // light-green 800
	monoline:     { label: 'Monoline',     bg: '#ef6c00', fg: '#fff' },  // orange 800
	multiline:    { label: 'Multiline',    bg: '#d84315', fg: '#fff' },  // deep-orange 800
	masterbrand:  { label: 'Masterbrand',  bg: '#8e24aa', fg: '#fff' },  // purple 600
}
const COMPOSITIONS = Object.keys(COMPOSITION_PALETTE)

function compositionStyle(c) {
	return COMPOSITION_PALETTE[c] || null
}


// Quick-filter chip slots — closed lists that the user can multi-select
// and the back ORs together. Unlike Media Type and Intent (each chip
// gets its own colour because the cards echo them), these are quasi-
// search shortcuts so a single uniform colour per row is enough.
//
// Categories list reflects the cosmetics-retail taxonomy in use today —
// expand here when the AI normaliser starts emitting new buckets.
const CATEGORY_SLOTS = [
	'fragrances', 'makeup', 'skincare', 'haircare', 'bodycare',
	'nails', 'mens', 'gifts', 'accessories', 'other',
]
const CATEGORY_COLOR = { bg: '#00838f', fg: '#fff' }   // cyan 800

function categoryStyle(c) {
	// Title-case the slot for display ("fragrances" → "Fragrances").
	const label = c ? c.charAt(0).toUpperCase() + c.slice(1) : c
	return { label, bg: CATEGORY_COLOR.bg, fg: CATEGORY_COLOR.fg }
}


// Sections are per retailer, so the row only exists once one is picked.
// Uniform colour for the same reason as categories: the cards do not echo it.
const SECTION_COLOR = { bg: '#455a64', fg: '#fff' }   // blue grey 700

function sectionStyle(id_section) {
	const s = sections.value.find(x => x.id_section === id_section)
	if (!s) return null
	const suffix = (s.kind === 'newsletter' ? ' (newsletter)' : '') + (s.active ? '' : ' · inactive')
	return { label: s.name + suffix, bg: SECTION_COLOR.bg, fg: SECTION_COLOR.fg }
}


const BRAND_COLOR = { bg: '#5d4037', fg: '#fff' }   // brown 700
// UNKNOWN BRAND gets its own colour: it is a real, clickable filter — the
// queue of resources whose brand the matcher could not recognise, which is
// where fixing them starts — but it is not a brand, and painting it in the
// brand palette would read as one.
const UNKNOWN_BRAND_ID = 0
const UNKNOWN_COLOR = { bg: '#b45309', fg: '#fff' }   // amber 700

// How many brand chips stay on the row. The rest live behind "+N", which is
// not a leftovers bin: measured on retail, the top 15 cover only 30% of the
// catalogue's mass (3.238 brands, 24% of them appearing exactly once), so the
// overflow search is the main way to reach a brand, not an afterthought.
// Raising the cut does not help — top 50 reaches 52%, top 100 only 65%.
const BRAND_CHIP_SLOTS = 15


// How long the search box stays quiet before the list reloads. Long enough to
// swallow a burst of typing, short enough that a deliberate pause still feels
// immediate.
const SEARCH_DEBOUNCE_MS = 350

// Monotonic request ticket, module-level so it survives re-renders. See
// loadResources() for why answers are matched against it.
let loadTicket = 0


export default function ResourcesPage() {
	const { showToast } = useAppToast()

	const loadRetailers = async () => {
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers`)
		if (!r.ok) return showToast.error('Failed to load retailers: ' + r.error)
		retailers.value = r.data?.items || []
	}

	const loadSections = async (id_retailer) => {
		sections.value = []
		if (!id_retailer) return
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers/${id_retailer}/sections`)
		if (!r.ok) return showToast.error('Failed to load sections: ' + r.error)
		// A late answer for a retailer the operator already left must not
		// repopulate the picker.
		if (selectedRetailerId.value !== id_retailer) return
		sections.value = r.data?.items || []
	}

	const loadResources = async () => {
		// Every call takes a ticket. Requests are never cancelled — the API
		// helper has no abort — so a slow early answer could otherwise land
		// after a fast later one and paint a list nobody asked for. Only the
		// most recent ticket is allowed to write the signals.
		const ticket = ++loadTicket
		isLoading.value = true
		const params = new URLSearchParams({
			page:  String(page.value),
			limit: String(limit.value),
		})
		if (selectedRetailerId.value) params.set('retailer', String(selectedRetailerId.value))
		if (selectedSectionId.value)  params.set('section',  String(selectedSectionId.value))
		if (selectedCountry.value)    params.set('country',  selectedCountry.value)
		if (selectedFamily.value)     params.set('family',   selectedFamily.value)
		if (selectedKind.value)       params.set('kind',     selectedKind.value)
		if (selectedParentKinds.value.length > 0) {
			params.set('parent_kind', selectedParentKinds.value.join(','))
		}
		if (selectedIntents.value.length > 0) {
			params.set('intent', selectedIntents.value.join(','))
		}
		if (selectedCompositions.value.length > 0) {
			params.set('composition', selectedCompositions.value.join(','))
		}
		if (selectedCategories.value.length > 0) {
			params.set('category', selectedCategories.value.join(','))
		}
		if (selectedBrands.value.length > 0) {
			params.set('brand_id', selectedBrands.value.join(','))
		}
		// Paging does not change the slice, so it cannot change the facets.
		// They are the expensive half of the response — skip them and keep
		// the ones already on screen.
		if (page.value > 1) params.set('facets', '0')
		if (selectedNeedsQa.value !== null) params.set('needs_qa', selectedNeedsQa.value ? '1' : '0')
		// ignored filter: false=live, true=ignored only, null=any
		if (selectedIgnored.value === null) params.set('ignored', 'any')
		else if (selectedIgnored.value)     params.set('ignored', '1')
		else                                 params.set('ignored', '0')
		if (minCountSeen.value && Number(minCountSeen.value) > 0) {
			params.set('min_count_seen', String(minCountSeen.value))
		}
		if (dateFrom.value) params.set('date_from', dateFrom.value)
		if (dateTo.value)   params.set('date_to',   dateTo.value)
		if (createdFrom.value) params.set('created_from', createdFrom.value)
		if (createdTo.value)   params.set('created_to',   createdTo.value)
		if (freeTextQuery.value)      params.set('q',     freeTextQuery.value)
		const r = await api.get(`${BACKEND_APIS.VT}/admin/resources?${params.toString()}`)
		// Superseded while in flight: a newer request owns the signals now, and
		// it is the one that will clear the spinner. Drop this answer silently —
		// including its error, which belongs to a slice nobody is looking at.
		if (ticket !== loadTicket) return
		isLoading.value = false
		if (!r.ok) return showToast.error('Failed to load resources: ' + r.error)
		items.value  = r.data?.items  || []
		counts.value = r.data?.counts || { needs_qa: 0, validated: 0 }
		total.value  = r.data?.total  || 0
		// `facets` is absent when we asked not to compute them (paging) and
		// null when the back could not (it degrades rather than 500ing the
		// list). Neither means "no brands" — only an actual array does.
		const facets = r.data?.facets
		if (facets?.brands) {
			brandFacets.value = facets.brands
			brandsUnresolved.value = facets.brands_unresolved || 0
			// Merge, never replace: a selected brand outside the current
			// slice still needs its name to render its chip.
			const merged = { ...brandNames.value }
			for (const b of facets.brands) merged[b.id_brand] = b.name
			brandNames.value = merged
		}
	}

	const openDetail = async (id_resource) => {
		detailLoading.value = true
		const r = await api.get(`${BACKEND_APIS.VT}/admin/resources/${id_resource}`)
		detailLoading.value = false
		if (!r.ok) return showToast.error('Failed to load resource: ' + r.error)
		detailResource.value = r.data
	}

	useEffect(() => {
		loadRetailers()
		loadResources()
		return clearResourcesSignals
	}, [])
	// Typing only moves `freeText`; the reload waits for it to settle here.
	// Clearing the box is treated as deliberate and applies at once, so the
	// full list comes back the moment the operator hits the ✕.
	useEffect(() => {
		const next = freeText.value.trim()
		if (!next) { freeTextQuery.value = ''; return }
		if (next === freeTextQuery.value) return
		const t = setTimeout(() => { freeTextQuery.value = next }, SEARCH_DEBOUNCE_MS)
		return () => clearTimeout(t)
	}, [freeText.value])
	useEffect(() => { loadResources() }, [
		selectedRetailerId.value, selectedSectionId.value, selectedCountry.value, selectedKind.value,
		selectedFamily.value,
		selectedParentKinds.value, selectedIntents.value, selectedCompositions.value,
		selectedCategories.value, selectedBrands.value,
		selectedNeedsQa.value, selectedIgnored.value, minCountSeen.value,
		dateFrom.value, dateTo.value, createdFrom.value, createdTo.value, freeTextQuery.value,
		page.value, limit.value,
	])
	useEffect(() => { loadSections(selectedRetailerId.value) }, [selectedRetailerId.value])

	return <PageWrapper
		{...vtPageProps('Resources', 'Master catalogue of unique creatives deduped via pHash')}
	>
		<Filters />
		<HeaderStats />
		<MergeBar onMerged={loadResources} />
		<MergeCandidatesBar onMerged={loadResources} />
		<AdminBar />
		<Box>
			{isLoading.value ? (
				<Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
					<CircularProgress size={22} />
					<Typography variant="body2">Loading resources…</Typography>
				</Stack>
			) : items.value.length === 0 ? (
				<Alert severity="info">No resources match these filters.</Alert>
			) : (
				<ResourcesGrid openDetail={openDetail} />
			)}
		</Box>
		<Pagination />
		<DetailDrawer />
	</PageWrapper>
}


// One-shot admin bar with destructive bulk actions. Two modes:
//   - Retailer selected → "Re-export retailer (all dates)" with an
//     optional section-kind scope picker (All / Web / Newsletter).
//   - No retailer selected → "Re-export ALL retailers" for the
//     currently-picked section-kind. Requires a kind to be set so an
//     unguarded click can't fire off "re-export the entire history".
// Kept out of the merge strip so two unrelated destructive actions
// don't share a row.
function AdminBar() {
	const { showToast } = useAppToast()
	const [submitting, setSubmitting] = useState(false)
	const [scopeKind, setScopeKind] = useState('')  // '' = All, 'web', 'newsletter'
	const id_retailer = selectedRetailerId.value
	const retailerSlug = id_retailer
		? (retailers.value.find(r => r.id_retailer === id_retailer)?.slug || id_retailer)
		: null
	const kindLabel = scopeKind || 'all'
	const reExportRetailer = async () => {
		const confirmed = window.confirm(
			`Re-export EVERY reviewed task of retailer "${retailerSlug}" ` +
			`(scope: ${kindLabel}) across ALL dates? ` +
			`This overwrites the affected CSVs in GCS and re-notifies the consumer.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const qs = scopeKind ? `?section_kind=${scopeKind}` : ''
		const r = await api.post(`${BACKEND_APIS.VT}/admin/retailers/${id_retailer}/export-all${qs}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-export failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	const reExportAllRetailers = async () => {
		if (!scopeKind) return showToast.warning(
			'Pick a section kind (web / newsletter) before re-exporting across all retailers.'
		)
		const confirmed = window.confirm(
			`Re-export EVERY reviewed task of EVERY retailer with section kind "${scopeKind}", ` +
			`across ALL dates? This may queue thousands of Cloud Tasks.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/export-bulk?section_kind=${scopeKind}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-export failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	// Re-match: same scope semantics as re-export but feeds the
	// resource matcher instead of the CSV exporter. Recovery action —
	// idempotent. Default `stages` on the backend covers scored /
	// matched / ai_reviewed / reviewed, so a task with leaves missing
	// `matched_resource_id` gets picked up regardless of how far down
	// the pipeline it advanced.
	const reMatchRetailer = async () => {
		const confirmed = window.confirm(
			`Re-match EVERY task of retailer "${retailerSlug}" (scope: ${kindLabel}) across ALL dates? ` +
			`Idempotent — safe to repeat.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const qs = new URLSearchParams()
		qs.set('id_retailer', id_retailer)
		if (scopeKind) qs.set('section_kind', scopeKind)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/re-match-bulk?${qs.toString()}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-match failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	const reMatchAllRetailers = async () => {
		if (!scopeKind) return showToast.warning(
			'Pick a section kind (web / newsletter) before re-matching across all retailers.'
		)
		const confirmed = window.confirm(
			`Re-match EVERY task of EVERY retailer with section kind "${scopeKind}", across ALL dates? ` +
			`This may queue thousands of Cloud Tasks.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/re-match-bulk?section_kind=${scopeKind}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-match failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Queued ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	// Re-score: recalculates visibility_score on each task's
	// results.json. Auto-fires re-export for tasks already at
	// `reviewed` (see v1.24.x changelog) so the CSV in GCS picks up
	// the new numbers without a second click. Sequential on the back
	// (Flask context / ORM) — bulks of several hundred tasks finish
	// in ~minutes.
	const reScoreRetailer = async () => {
		const confirmed = window.confirm(
			`Re-score EVERY task of retailer "${retailerSlug}" (scope: ${kindLabel}) across ALL dates? ` +
			`Auto re-exports the reviewed ones afterwards.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const qs = new URLSearchParams()
		qs.set('id_retailer', id_retailer)
		if (scopeKind) qs.set('section_kind', scopeKind)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/rescore-bulk?${qs.toString()}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-score failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Re-scored ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	const reScoreAllRetailers = async () => {
		if (!scopeKind) return showToast.warning(
			'Pick a section kind (web / newsletter) before re-scoring across all retailers.'
		)
		const confirmed = window.confirm(
			`Re-score EVERY task of EVERY retailer with section kind "${scopeKind}", across ALL dates? ` +
			`Auto re-exports reviewed tasks afterwards. May take a while.`
		)
		if (!confirmed) return
		setSubmitting(true)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/tasks/rescore-bulk?section_kind=${scopeKind}`)
		setSubmitting(false)
		if (!r.ok) return showToast.error('Re-score failed: ' + r.error)
		const ok = r.data?.ok || 0
		const failed = r.data?.failed || 0
		showToast.success(`Re-scored ${ok} task(s)` + (failed ? ` (${failed} failed)` : ''))
	}
	return <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
		<TextField select size="small" label="Re-export scope" sx={{ minWidth: 160 }}
			value={scopeKind}
			onChange={e => setScopeKind(e.target.value)}
		>
			<MenuItem value="">All section kinds</MenuItem>
			<MenuItem value="web">web only</MenuItem>
			<MenuItem value="newsletter">newsletter only</MenuItem>
		</TextField>
		{id_retailer ? (
			<>
				<Tooltip title={`POST /admin/retailers/${id_retailer}/export-all — bulk re-export every reviewed task for this retailer across every date, scope ${kindLabel}.`}>
					<span>
						<Button size="small" variant="outlined" color="warning"
							disabled={submitting}
							onClick={reExportRetailer}
						>Re-export retailer (all dates)</Button>
					</span>
				</Tooltip>
				<Tooltip title="POST /admin/tasks/re-match-bulk — re-enqueue the resource matcher for every task of this retailer across every date. Recovery action, idempotent.">
					<span>
						<Button size="small" variant="outlined" color="warning"
							disabled={submitting}
							onClick={reMatchRetailer}
						>Re-match retailer (all dates)</Button>
					</span>
				</Tooltip>
				<Tooltip title="POST /admin/tasks/rescore-bulk — recalc visibility_score for every task of this retailer across every date. Auto re-exports the reviewed ones.">
					<span>
						<Button size="small" variant="outlined" color="warning"
							disabled={submitting}
							onClick={reScoreRetailer}
						>Re-score retailer (all dates)</Button>
					</span>
				</Tooltip>
			</>
		) : (
			<>
				<Tooltip title="POST /admin/export-bulk — every retailer, every date, scoped by the section kind picker on the left. Requires a kind to be set.">
					<span>
						<Button size="small" variant="outlined" color="error"
							disabled={submitting || !scopeKind}
							onClick={reExportAllRetailers}
						>Re-export ALL retailers (every date)</Button>
					</span>
				</Tooltip>
				<Tooltip title="POST /admin/tasks/re-match-bulk — re-enqueue the matcher across every retailer, every date, scoped by the section kind picker. Requires a kind to be set.">
					<span>
						<Button size="small" variant="outlined" color="error"
							disabled={submitting || !scopeKind}
							onClick={reMatchAllRetailers}
						>Re-match ALL retailers (every date)</Button>
					</span>
				</Tooltip>
				<Tooltip title="POST /admin/tasks/rescore-bulk — recalc visibility_score across every retailer, every date, scoped by the section kind picker. Auto re-exports reviewed tasks. Requires a kind to be set.">
					<span>
						<Button size="small" variant="outlined" color="error"
							disabled={submitting || !scopeKind}
							onClick={reScoreAllRetailers}
						>Re-score ALL retailers (every date)</Button>
					</span>
				</Tooltip>
			</>
		)}
	</Stack>
}


// The answer to "why are these apart?": each selected resource as the
// matcher sees it, one verdict per pair, and the decisions that already
// moved sightings between them. The verdict comes from the back in one
// sentence so this component only lays it out; a green pair is one the
// rules would merge — the Merge button is offered when every pair is.
function WhyApartDialog({ why, onClose, onMerge }) {
	if (!why) return null
	const byId = Object.fromEntries((why.recursos || []).map(r => [r.id_resource, r]))
	const fmtTs = (ts) => ts ? ts.replace('T', ' ').slice(0, 16) : ''
	return <Dialog open onClose={onClose} maxWidth="md" fullWidth>
		<Box sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
				<HelpOutlineIcon color="primary" />
				<Typography variant="h6" sx={{ flex: 1 }}>Why are these apart?</Typography>
				<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
			</Stack>

			<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
				{(why.recursos || []).map(r => (
					<Paper key={r.id_resource} variant="outlined" sx={{ p: 1, minWidth: 220, flex: '1 1 220px' }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							#{r.id_resource} <Chip size="small" label={r.familia} sx={{ ml: 0.5 }} />
							{r.leaf_kind && <Chip size="small" variant="outlined" label={r.leaf_kind} sx={{ ml: 0.5 }} />}
						</Typography>
						<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
							{r.retailer} · seen {r.count_seen}× · pHash {r.phash}
						</Typography>
						{r.product && <Typography variant="body2" sx={{ mt: 0.5 }}>
							“{r.product}”
							{r.product_norm && <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>→ {r.product_norm}</Typography>}
						</Typography>}
						<Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3, mt: 0.5 }}>
							{(r.brands || []).map(b => <Chip key={`${r.id_resource}-${b.id_brand}-${b.name}`} size="small" variant="outlined" color="secondary" label={b.name || `#${b.id_brand}`} />)}
							{(r.brands || []).length === 0 && <Chip size="small" label="no brand" />}
						</Stack>
						{r.qa_reason && <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.5 }}>{r.qa_reason}</Typography>}
					</Paper>
				))}
			</Stack>
			{(why.no_encontrados || []).length > 0 && (
				<Alert severity="warning" sx={{ mb: 1 }}>Not found: {why.no_encontrados.join(', ')}</Alert>
			)}

			<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Verdict per pair</Typography>
			<Stack spacing={1} sx={{ mb: 2 }}>
				{(why.pares || []).map(p => (
					<Alert key={`${p.a}-${p.b}`} severity={p.fusionable ? 'success' : 'info'} icon={false} sx={{ py: 0.5 }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>#{p.a} ↔ #{p.b}</Typography>
						<Typography variant="body2">{p.veredicto}</Typography>
						<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3 }}>
							{p.phash_distancia != null && <>pHash distance {p.phash_distancia} · </>}
							{p.texto?.similitud != null && <>title similarity {p.texto.similitud} · </>}
							{p.texto?.mismas_marcas ? 'same brands' : `brands: ${p.texto?.marcas_a} vs ${p.texto?.marcas_b}`}
							{p.imagen && <> · capture rule: formato {p.imagen.formato_ok ? 'ok' : 'no'}, ORB {p.imagen.orb}{p.imagen.mad != null && `, MAD ${p.imagen.mad}`}{p.imagen.bloque != null && `, cell ${p.imagen.bloque}`}{p.imagen.veto && ` → veto ${p.imagen.veto}`}</>}
							{(p.fichero_a || p.fichero_b) && <> · image file {p.fichero_a && p.fichero_a === p.fichero_b ? 'same' : 'different / partial'}</>}
							{p.jev && (p.jev.fuera_de_alcance
								? <> · Jev: out of scope ({p.jev.fuera_de_alcance})</>
								: <> · Jev: same {p.jev.p_same} · related {p.jev.p_related} · different {p.jev.p_different}</>)}
						</Typography>
					</Alert>
				))}
			</Stack>

			{((why.historia?.enlaces || []).length > 0 || (why.historia?.texto || []).length > 0) && <>
				<Typography variant="subtitle2" sx={{ mb: 0.5 }}>What already happened between them</Typography>
				<Stack spacing={0.3} sx={{ mb: 2 }}>
					{(why.historia.enlaces || []).map((e, i) => (
						<Typography key={`e${i}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
							{fmtTs(e.ts)} · {e.evento} · {e.regla} · by {e.decidido_por}{e.actor ? ` (${e.actor})` : ''} · #{e.de} → #{e.a}{e.distancia_phash != null ? ` · d=${e.distancia_phash}` : ''}{e.nota ? ` · ${e.nota}` : ''}
						</Typography>
					))}
					{(why.historia.texto || []).map((t, i) => (
						<Typography key={`t${i}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
							{fmtTs(t.ts)} · text match: hash said #{t.hash_target} ({t.hash}), text said #{t.text_target}{t.clave ? ` · ${t.clave}` : ''}{t.enforced ? ' · enforced' : ' · shadow'}
						</Typography>
					))}
				</Stack>
			</>}

			<Stack direction="row" justifyContent="flex-end" spacing={1}>
				<Button onClick={onClose}>Close</Button>
				{why.todos_fusionables && Object.keys(byId).length >= 2 && (
					<Button variant="contained" color="primary" startIcon={<CallMergeIcon fontSize="small" />} onClick={onMerge}>
						Merge these
					</Button>
				)}
			</Stack>
		</Box>
	</Dialog>
}

// The merge-candidate queue (869eu5kp4, plan cola-candidatos-fusion): the
// pairs the check mode would paint green, found overnight by the back and
// listed here with both crops so QA merges or rejects by eye instead of
// hunting them in Merge Mode. Nothing merges on its own; a rejected pair
// is never proposed again. Filters: the page's retailer and family.
const CANDIDATE_FAMILY = { product: 'producto', media: 'media' }

function MergeCandidatesBar({ onMerged }) {
	const { showToast } = useAppToast()
	const [open, setOpen] = useState(false)
	const [pending, setPending] = useState(null)
	const [queue, setQueue] = useState({ items: [], total: 0, offset: 0 })
	const [loading, setLoading] = useState(false)
	const [busy, setBusy] = useState(null)       // id_candidate being decided, or 'bulk'
	const [showAll, setShowAll] = useState(false) // also the non-green pairs
	const [picked, setPicked] = useState(() => new Set()) // id_candidates ticked for a bulk decision
	const retailerId = selectedRetailerId.value
	const family = CANDIDATE_FAMILY[selectedFamily.value] || null
	const PAGE = 20

	// The page's own filters travel with the queue (brands, kinds, dates, min
	// seen, needs QA, free text): a pair shows when either resource matches.
	const pageFilters = () => {
		const p = new URLSearchParams()
		if (selectedKind.value) p.set('kind', selectedKind.value)
		if (selectedParentKinds.value.length > 0) p.set('parent_kind', selectedParentKinds.value.join(','))
		if (selectedBrands.value.length > 0) p.set('brand_id', selectedBrands.value.join(','))
		if (selectedNeedsQa.value !== null) p.set('needs_qa', selectedNeedsQa.value ? '1' : '0')
		if (minCountSeen.value && Number(minCountSeen.value) > 0) p.set('min_count_seen', String(minCountSeen.value))
		if (dateFrom.value) p.set('date_from', dateFrom.value)
		if (dateTo.value) p.set('date_to', dateTo.value)
		if (createdFrom.value) p.set('created_from', createdFrom.value)
		if (createdTo.value) p.set('created_to', createdTo.value)
		if (freeTextQuery.value) p.set('q', freeTextQuery.value)
		return p
	}
	const filterKey = pageFilters().toString()
	const params = (offset) => {
		const p = pageFilters()
		p.set('limit', String(PAGE)); p.set('offset', String(offset)); p.set('status', 'pending')
		if (retailerId) p.set('retailer', String(retailerId))
		if (family) p.set('family', family)
		return p.toString()
	}

	const load = async (offset = 0) => {
		setLoading(true)
		try {
			const r = await api.get(`${BACKEND_APIS.VT}/admin/resources/merge-candidates?${params(offset)}`)
			if (!r.ok) {
				showToast.error('Candidates failed: ' + r.error)
				return
			}
			const d = r.data || {}
			setQueue({ items: d.items || [], total: d.total || 0, offset })
			setPending(d.pending || {})
			setPicked(new Set())
		} finally {
			setLoading(false)
		}
	}

	// The badge: pending pairs for the current retailer/family, refreshed when
	// the filter changes. The list itself loads when the drawer opens.
	useEffect(() => {
		let alive = true
		;(async () => {
			const r = await api.get(`${BACKEND_APIS.VT}/admin/resources/merge-candidates?${params(0)}&limit=1`)
			if (alive && r.ok) setPending(r.data?.pending || {})
		})()
		return () => { alive = false }
	}, [retailerId, family, filterKey])
	useEffect(() => { if (open) load(0) }, [open, retailerId, family, filterKey])

	const decide = async (c, action) => {
		setBusy(c.id_candidate)
		try {
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/merge-candidates/${c.id_candidate}/${action}`, {})
			if (!r.ok) {
				showToast.error(`${action === 'merge' ? 'Merge' : 'Reject'} failed: ` + r.error)
				return
			}
			if (action === 'merge') {
				showToast.success(`Merged #${c.a.id_resource} + #${c.b.id_resource} into #${r.data?.target_id}`)
				if (onMerged) onMerged()
			}
			// Drop the row locally and keep the page full from the next offset.
			const rest = queue.items.filter(x => x.id_candidate !== c.id_candidate)
			setQueue(q => ({ ...q, items: rest, total: Math.max(0, q.total - 1) }))
			setPending(p => {
				const n = { ...(p || {}) }
				n[c.familia] = Math.max(0, (n[c.familia] || 1) - 1)
				return n
			})
			if (rest.length === 0 && queue.total > 1) load(0)
		} finally {
			setBusy(null)
		}
	}

	// Several pairs, one call: the back decides them one by one (each merge
	// commits on its own) and answers which went through and which did not,
	// so the operator ticks a page and leaves instead of waiting per pair.
	const decideBulk = async (action) => {
		const ids = queue.items.filter(c => picked.has(c.id_candidate)).map(c => c.id_candidate)
		if (ids.length === 0) return
		setBusy('bulk')
		try {
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/merge-candidates/bulk`, { ids, action })
			if (!r.ok) {
				showToast.error(`Bulk ${action} failed: ` + r.error)
				return
			}
			const done = new Set((r.data?.done || []).map(d => d.id_candidate))
			const failed = r.data?.failed || []
			if (failed.length) {
				showToast.error(`${failed.length} pair(s) failed: ` + failed.map(f => `#${f.id_candidate} ${f.error}`).join(' · '))
			}
			if (done.size) {
				showToast.success(`${action === 'merge' ? 'Merged' : 'Rejected'} ${done.size} pair(s)`)
				if (action === 'merge' && onMerged) onMerged()
			}
			const decided = queue.items.filter(c => done.has(c.id_candidate))
			const rest = queue.items.filter(c => !done.has(c.id_candidate))
			setQueue(q => ({ ...q, items: rest, total: Math.max(0, q.total - done.size) }))
			setPending(p => {
				const n = { ...(p || {}) }
				decided.forEach(c => { n[c.familia] = Math.max(0, (n[c.familia] || 1) - 1) })
				return n
			})
			setPicked(new Set())
			if (rest.length === 0 && queue.total > done.size) load(0)
		} finally {
			setBusy(null)
		}
	}
	const togglePick = (id) => setPicked(p => {
		const n = new Set(p)
		if (n.has(id)) n.delete(id); else n.add(id)
		return n
	})

	const nPending = Object.values(pending || {}).reduce((s, n) => s + n, 0)
	const visible = showAll ? queue.items : queue.items.filter(c => c.fusionable)
	const hidden = queue.items.length - visible.length
	const nPicked = visible.filter(c => picked.has(c.id_candidate)).length
	const allGreenPicked = visible.filter(c => c.fusionable).every(c => picked.has(c.id_candidate))

	return <>
		<Box sx={{ mt: 1 }}>
			<Button size="small" variant="outlined" color="success"
				startIcon={<CheckCircleIcon fontSize="small" />}
				onClick={() => setOpen(true)}>
				Candidates{pending ? ` (${nPending})` : ''}
			</Button>
		</Box>
		<Drawer anchor="right" open={open} onClose={() => setOpen(false)}
			PaperProps={{ sx: { width: { xs: '100%', md: 760 } } }}>
			<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
					<CheckCircleIcon color="success" />
					<Typography variant="h6" sx={{ flex: 1 }}>Merge candidates</Typography>
					<Chip size="small" label={`${queue.total} pending`} />
					<Button size="small" onClick={() => setShowAll(v => !v)}>
						{showAll ? 'Green only' : `Show all${hidden ? ` (+${hidden})` : ''}`}
					</Button>
					<IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
				</Stack>
				<Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5 }}>
					Pairs the matcher kept apart that the identity rules (title, brands, Jev, capture rule, image file)
					say are the same. Look at both crops: <b>Merge</b> collapses them, <b>Not the same</b> hides the pair for good.
					{!retailerId && ' Pick a retailer to narrow the queue.'}
				</Typography>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
					<Button size="small" variant="outlined" disabled={busy != null || visible.length === 0}
						onClick={() => setPicked(allGreenPicked
							? new Set()
							: new Set(visible.filter(c => c.fusionable).map(c => c.id_candidate)))}>
						{allGreenPicked && nPicked > 0 ? 'Clear selection' : 'Select all green'}
					</Button>
					<Chip size="small" label={`${nPicked} picked`} color={nPicked > 0 ? 'primary' : 'default'} />
					<Box sx={{ flex: 1 }} />
					<Button size="small" variant="contained" color="primary"
						startIcon={busy === 'bulk' ? <CircularProgress size={14} /> : <CallMergeIcon fontSize="small" />}
						disabled={busy != null || nPicked === 0} onClick={() => decideBulk('merge')}>
						Merge selected{nPicked ? ` (${nPicked})` : ''}
					</Button>
					<Button size="small" variant="outlined" color="inherit"
						disabled={busy != null || nPicked === 0} onClick={() => decideBulk('reject')}>
						Not the same{nPicked ? ` (${nPicked})` : ''}
					</Button>
				</Stack>
				{loading && queue.items.length === 0 ? (
					<Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
						<CircularProgress size={22} /><Typography variant="body2">Loading candidates…</Typography>
					</Stack>
				) : visible.length === 0 ? (
					<Alert severity="info">{queue.items.length === 0 ? 'No pending candidates for these filters.' : 'No green pairs on this page — use "Show all" to see the doubtful ones.'}</Alert>
				) : (
					<Stack spacing={1.5} sx={{ overflowY: 'auto', flex: 1 }}>
						{visible.map(c => (
							<Paper key={c.id_candidate} variant="outlined"
								sx={{ p: 1.5, borderColor: picked.has(c.id_candidate) ? 'primary.main' : (c.fusionable ? 'success.main' : 'divider'),
									bgcolor: picked.has(c.id_candidate) ? 'primary.50' : 'background.paper' }}>
								<Stack direction="row" spacing={1.5} alignItems="flex-start">
									<Checkbox size="small" checked={picked.has(c.id_candidate)} disabled={busy != null}
										onChange={() => togglePick(c.id_candidate)} sx={{ p: 0.5, mt: -0.5 }} />
									{[c.a, c.b].map(r => (
										<Box key={r.id_resource} sx={{ width: 200 }}>
											<ResourceThumb src={r.crop_inlined} maxSize={200} />
											<Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
												#{r.id_resource} · seen {r.count_seen ?? '?'}×{r.leaf_kind ? ` · ${r.leaf_kind}` : ''}
											</Typography>
											{r.product && <Typography variant="caption" sx={{ display: 'block' }}>“{r.product}”</Typography>}
											<Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
												{(r.brands || []).map(b => <Chip key={`${r.id_resource}-${b.id_brand}`} size="small" variant="outlined" color="secondary" label={b.name || `#${b.id_brand}`} />)}
											</Stack>
										</Box>
									))}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.3 }}>
											<Chip size="small" color={c.fusionable ? 'success' : 'default'} label={c.fusionable ? 'green' : 'doubtful'} />
											<Chip size="small" variant="outlined" label={c.regla} />
											{c.score != null && <Chip size="small" variant="outlined" label={c.regla === 'product-title-truncated' ? `pHash ${c.score}` : `score ${c.score}`} />}
											{c.jev_p_same != null && <Chip size="small" variant="outlined" label={`Jev same ${c.jev_p_same}`} />}
											<Chip size="small" variant="outlined" label={c.retailer} />
										</Stack>
										<Typography variant="body2">{c.veredicto}</Typography>
										<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
											<Button size="small" variant="contained" color="primary"
												startIcon={busy === c.id_candidate ? <CircularProgress size={14} /> : <CallMergeIcon fontSize="small" />}
												disabled={busy != null} onClick={() => decide(c, 'merge')}>
												Merge
											</Button>
											<Button size="small" variant="outlined" color="inherit"
												disabled={busy != null} onClick={() => decide(c, 'reject')}>
												Not the same
											</Button>
										</Stack>
									</Box>
								</Stack>
							</Paper>
						))}
					</Stack>
				)}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
					<Button size="small" disabled={loading || queue.offset === 0} onClick={() => load(Math.max(0, queue.offset - PAGE))}>Previous</Button>
					<Typography variant="caption">{queue.total ? `${queue.offset + 1}–${Math.min(queue.offset + PAGE, queue.total)} of ${queue.total}` : ''}</Typography>
					<Button size="small" disabled={loading || queue.offset + PAGE >= queue.total} onClick={() => load(queue.offset + PAGE)}>Next</Button>
				</Stack>
			</Box>
		</Drawer>
	</>
}

// Header strip for merge mode. Toggle "Merge mode" on/off; when on,
// shows the running selection count and lets the operator hit Merge
// (opens a confirm dialog) or Clear. Hidden when the toggle is off
// so the dashboard stays clean for read-only browsing.
function MergeBar({ onMerged }) {
	const { showToast } = useAppToast()
	const [confirming, setConfirming] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	// Check mode (869eu5kp4, 24-09): "why are these apart?". The back
	// compares what the identity rules compare and reads the audit; the
	// dialog shows one verdict per pair. Read-only until the operator
	// decides to merge from it.
	const [why, setWhy] = useState(null)
	const [whyLoading, setWhyLoading] = useState(false)
	const on = mergeMode.value
	const count = mergeSelection.value.size
	// Pre-validate the selection on the client: same retailer is
	// required by the back; we show the constraint inline so the
	// operator doesn't get a 400 from the dialog.
	const selectedItems = items.value.filter(r => mergeSelection.value.has(r.id_resource))
	const retailerIds = new Set(selectedItems.map(r => r.id_retailer))
	const sameRetailer = retailerIds.size <= 1
	const target = selectedItems.length > 0
		? [...selectedItems].sort((a, b) => (
			(b.count_seen || 0) - (a.count_seen || 0) ||
			a.id_resource - b.id_resource
		))[0]
		: null

	const toggleMode = () => {
		mergeMode.value = !mergeMode.value
		if (!mergeMode.value) {
			mergeSelection.value = new Set()
		}
	}

	const clear = () => {
		mergeSelection.value = new Set()
	}

	const askWhy = async () => {
		setWhyLoading(true)
		try {
			const ids = [...mergeSelection.value]
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/why-apart`, { ids })
			if (!r.ok) {
				showToast.error('Check failed: ' + r.error)
				return
			}
			setWhy(r.data)
		} finally {
			setWhyLoading(false)
		}
	}

	const submit = async () => {
		setSubmitting(true)
		try {
			const ids = [...mergeSelection.value]
			const r = await api.post(`${BACKEND_APIS.VT}/admin/resources/merge`, { ids })
			if (!r.ok) {
				showToast.error('Merge failed: ' + r.error)
				return
			}
			const d = r.data || {}
			showToast.success(
				`Merged ${ids.length} resources into #${d.target_id} · ` +
				`${d.occurrences_relinked} occurrence(s) re-linked`
			)
			mergeSelection.value = new Set()
			mergeMode.value = false
			setConfirming(false)
			if (onMerged) onMerged()
		} finally {
			setSubmitting(false)
		}
	}

	if (!on) {
		return <Box sx={{ mt: 1 }}>
			<Button size="small" variant="outlined" startIcon={<CallMergeIcon fontSize="small" />}
				onClick={toggleMode}>
				Merge mode
			</Button>
		</Box>
	}

	return <>
		<Paper variant="outlined" sx={{
			mt: 1, p: 1.5,
			bgcolor: count > 0 ? 'primary.50' : 'background.paper',
			borderColor: count > 0 ? 'primary.main' : 'divider',
			display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
		}}>
			<CallMergeIcon fontSize="small" color="primary" />
			<Typography variant="body2" sx={{ fontWeight: 600 }}>
				Merge mode — pick the duplicates you want to collapse
			</Typography>
			<Chip size="small"
				label={`${count} selected`}
				color={count >= 2 ? 'primary' : 'default'}
			/>
			{count > 0 && !sameRetailer && (
				<Alert severity="warning" sx={{ py: 0, my: 0 }}>
					Selection spans {retailerIds.size} retailers — merge needs same retailer.
				</Alert>
			)}
			<Box sx={{ flex: 1 }} />
			<Button size="small" onClick={clear} disabled={count === 0 || submitting}>Clear</Button>
			<Button size="small" onClick={toggleMode} disabled={submitting}>Exit</Button>
			<Tooltip title="Compare the selected resources the way the matcher does (title, brands, capture rule) and read what already happened between them">
				<span>
					<Button size="small" variant="outlined"
						startIcon={whyLoading ? <CircularProgress size={14} /> : <HelpOutlineIcon fontSize="small" />}
						disabled={count < 2 || count > 12 || submitting || whyLoading}
						onClick={askWhy}>
						Why apart?
					</Button>
				</span>
			</Tooltip>
			<Button size="small" variant="contained" color="primary"
				startIcon={<CallMergeIcon fontSize="small" />}
				disabled={count < 2 || !sameRetailer || submitting}
				onClick={() => setConfirming(true)}>
				Merge {count > 1 ? `(${count})` : ''}
			</Button>
		</Paper>

		<WhyApartDialog why={why} onClose={() => setWhy(null)}
			onMerge={() => { setWhy(null); setConfirming(true) }} />

		<Dialog open={confirming} onClose={() => !submitting && setConfirming(false)} maxWidth="sm" fullWidth>
			<Box sx={{ p: 2 }}>
				<Typography variant="h6" gutterBottom>Confirm merge</Typography>
				<Typography variant="body2" sx={{ mb: 2 }}>
					{count} resources will collapse into a single canonical row.
					The target keeps its tagging; the rest become tombstones
					(future captures matching their pHash will redirect to
					the target).
				</Typography>
				{target && (
					<Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
						<Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.05, fontSize: 10 }}>
							Auto-picked target (highest count_seen)
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
							{target.crop_inlined && (
								<Box component="img" src={target.crop_inlined}
									sx={{ width: 64, height: 48, objectFit: 'contain', bgcolor: 'grey.100' }}
								/>
							)}
							<Box>
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									#{target.id_resource}
								</Typography>
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									seen {target.count_seen}× · pHash {target.phash}
								</Typography>
							</Box>
						</Stack>
					</Paper>
				)}
				<Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 1 }}>
					Sources ({selectedItems.length - 1}):
				</Typography>
				<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
					{selectedItems.filter(r => target && r.id_resource !== target.id_resource).map(r => (
						<Tooltip key={r.id_resource} title={`#${r.id_resource} · seen ${r.count_seen}×`}>
							{r.crop_inlined
								? <Box component="img" src={r.crop_inlined}
									sx={{ width: 48, height: 36, objectFit: 'contain', bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}
								/>
								: <Chip size="small" label={`#${r.id_resource}`} variant="outlined" />
							}
						</Tooltip>
					))}
				</Stack>
				<Stack direction="row" justifyContent="flex-end" spacing={1}>
					<Button onClick={() => setConfirming(false)} disabled={submitting}>Cancel</Button>
					<Button variant="contained" color="primary" onClick={submit} disabled={submitting}>
						{submitting ? 'Merging…' : 'Merge'}
					</Button>
				</Stack>
			</Box>
		</Dialog>
	</>
}


function Filters() {
	// Layout reads top-to-bottom by importance:
	//   1. Search — what / who (free-text on brands, cats, type, retailer).
	//   2. Where + when — retailer dropdown + date range.
	//   3. Type axes — Media Type and Intent chip rows, each labelled.
	// We deliberately leave QA / count-seen / clear-all OUT of the Paper
	// — they sit in the header strip below as inline-flow controls so
	// the filter card itself stays focused on "which slice am I looking
	// at" instead of becoming a kitchen sink.
	const anyActive = (
		freeText.value
		|| selectedRetailerId.value || selectedSectionId.value
		|| selectedCountry.value || selectedKind.value
		|| selectedFamily.value
		|| selectedParentKinds.value.length > 0
		|| selectedIntents.value.length > 0
		|| selectedCompositions.value.length > 0
		|| selectedCategories.value.length > 0
		|| selectedBrands.value.length > 0
		|| selectedNeedsQa.value !== null
		|| (minCountSeen.value && Number(minCountSeen.value) > 0)
	)
	const clearAll = () => {
		freeText.value = ''
		selectedRetailerId.value = null
		selectedCountry.value = ''
		selectedSectionId.value = null
		selectedKind.value = null
		selectedFamily.value = null
		selectedParentKinds.value = []
		selectedIntents.value = []
		selectedCompositions.value = []
		selectedCategories.value = []
		selectedBrands.value = []
		// The brand row has state of its own now — the overflow panel and its
		// search box. Leaving them behind meant "Clear filters" left an open
		// popover filtered by a word that no longer applied to anything.
		brandsExpanded.value = false
		brandFilter.value = ''
		selectedNeedsQa.value = null
		minCountSeen.value = ''
		page.value = 1
	}
	// One generic toggle factory — every chip row uses the same
	// add-or-remove semantics, the only thing that changes is which
	// signal it writes to.
	const makeToggle = (sig) => (v) => {
		const cur = sig.value
		sig.value = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]
		page.value = 1
	}
	const toggleParentKind = makeToggle(selectedParentKinds)
	const toggleIntent     = makeToggle(selectedIntents)
	const toggleComposition = makeToggle(selectedCompositions)
	const toggleCategory   = makeToggle(selectedCategories)
	const toggleBrand      = makeToggle(selectedBrands)
	return <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
		<Stack spacing={1.8}>
			{/* Row 1 — primary search. Wide field with a leading icon
			    so it's instantly recognisable as the main entry point.
			    Includes a clear (×) adornment when populated. */}
			<TextField size="small" fullWidth
				placeholder="Search brand, category, type, retailer… — comma-separate for OR (e.g. Rabanne, Dior)"
				value={freeText.value}
				onChange={e => { freeText.value = e.target.value; page.value = 1 }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
						</InputAdornment>
					),
					endAdornment: freeText.value ? (
						<InputAdornment position="end">
							<IconButton size="small"
								onClick={() => { freeText.value = ''; page.value = 1 }}
							>
								<ClearIcon fontSize="small" />
							</IconButton>
						</InputAdornment>
					) : null,
					sx: { bgcolor: 'background.paper' },
				}}
			/>
			{/* Row 2 — context: retailer + date range. These constrain
			    *where* and *when*; they're not the primary thing the user
			    looks for, so they stay smaller and live on a single line. */}
			<Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
				<TextField select size="small" label="Country" sx={{ width: 130 }}
					value={selectedCountry.value}
					onChange={e => { selectedCountry.value = e.target.value; page.value = 1 }}
				>
					<MenuItem value="">All</MenuItem>
					{Array.from(new Set(retailers.value.map(r => (r.country || '').toUpperCase()).filter(Boolean))).sort().map(cc => (
						<MenuItem key={cc} value={cc}>{cc}</MenuItem>
					))}
				</TextField>
				<TextField select size="small" label="Retailer" sx={{ minWidth: 200 }}
					value={selectedRetailerId.value || ''}
					onChange={e => {
						selectedRetailerId.value = e.target.value ? Number(e.target.value) : null
						selectedSectionId.value  = null
						page.value = 1
					}}
				>
					<MenuItem value="">All retailers</MenuItem>
					{retailers.value.filter(r => r.active).map(r => (
						<MenuItem key={r.id_retailer} value={r.id_retailer}>{r.slug}</MenuItem>
					))}
				</TextField>
				<TextField size="small" type="date" label="From"
					value={dateFrom.value}
					onChange={e => { dateFrom.value = e.target.value; page.value = 1 }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 170 }}
				/>
				<TextField size="small" type="date" label="To"
					value={dateTo.value}
					onChange={e => { dateTo.value = e.target.value; page.value = 1 }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 170 }}
				/>
				<TextField size="small" type="date" label="Created from"
					value={createdFrom.value}
					onChange={e => { createdFrom.value = e.target.value; page.value = 1 }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 170 }}
				/>
				<TextField size="small" type="date" label="Created to"
					value={createdTo.value}
					onChange={e => { createdTo.value = e.target.value; page.value = 1 }}
					InputLabelProps={{ shrink: true }}
					sx={{ width: 170 }}
				/>
			</Stack>
			<Divider />
			{/* Rows 3 + 4 — the two type axes the page exists to slice by.
			    Same colour palette as the chips on each card, so picking
			    a chip here visibly mirrors what shows up below. */}
			{/* Family (R1) first: it is what the resource IS. The two rows
			    below describe where it appeared, which is per-occurrence
			    data — the same product shows up in product-list and in
			    product-slider, so it cannot lead the filtering. */}
			<ChipFilterRow
				label="Family"
				slots={FAMILIES}
				selected={selectedFamily.value ? [selectedFamily.value] : []}
				styleFor={familyStyle}
				/* One family per resource: picking another replaces it rather
				   than adding. Clicking the active one clears it. */
				onToggle={f => {
					selectedFamily.value = selectedFamily.value === f ? null : f
				}}
			/>
			<ChipFilterRow
				label="Media Type"
				slots={PARENT_KINDS}
				selected={selectedParentKinds.value}
				styleFor={parentKindStyle}
				onToggle={toggleParentKind}
			/>
			<ChipFilterRow
				label="Intent"
				slots={INTENTS}
				selected={selectedIntents.value}
				styleFor={intentStyle}
				onToggle={toggleIntent}
			/>
			<ChipFilterRow
				label="Composition"
				slots={COMPOSITIONS}
				selected={selectedCompositions.value}
				styleFor={compositionStyle}
				onToggle={toggleComposition}
			/>
			<ChipFilterRow
				label="Categories"
				slots={CATEGORY_SLOTS}
				selected={selectedCategories.value}
				styleFor={categoryStyle}
				onToggle={toggleCategory}
			/>
			{/* Single-select like Family: the back filters on one id_section
			    (any sighting in it, via the occurrence ledger). */}
			{sections.value.length > 0 && (
				<ChipFilterRow
					label="Sections"
					slots={sections.value.map(x => x.id_section)}
					selected={selectedSectionId.value ? [selectedSectionId.value] : []}
					styleFor={sectionStyle}
					onToggle={id => {
						selectedSectionId.value = selectedSectionId.value === id ? null : id
						page.value = 1
					}}
				/>
			)}
			<BrandFacetRow onToggle={toggleBrand} />
			{anyActive && (
				<Stack direction="row" justifyContent="flex-end">
					<Button size="small" onClick={clearAll} startIcon={<ClearIcon fontSize="small" />}>
						Clear filters
					</Button>
				</Stack>
			)}
		</Stack>
	</Paper>
}


// Coloured-chip filter row. Shared between the Media Type and Intent
// rows so both look and behave the same — the only thing that changes
// is the label, the slot list, and the palette function. Each chip:
// outlined when unselected, solid filled when selected, hover-tinted
// either way. Skips slots whose style is null (used when an "other"
// fallback exists but we don't want a chip for it).
// Brand chips, computed from what is actually on screen instead of a
// hand-maintained list. Ordering and counts come from the back, over the whole
// filtered set — not the current page, which would make the numbers lie.
// Tagging lives at two levels: on the resource, and inside each entry of
// `products`. Product-slider and product-list cards usually carry nothing at
// the top level, so reading only `tagging.brands` showed them as "(no
// tagging)" — and after the backend filter learned to see both levels, that
// became actively confusing: filtering by Rochas would surface cards with no
// Rochas visible anywhere, which reads as a broken filter.
function _brandLabel(b) {
	return typeof b === 'string' ? b : (b?.name || b?.brand_name)
}

function allTaggedBrands(tagging) {
	const out = []
	const seen = new Set()
	const push = (b) => {
		const label = _brandLabel(b)
		if (!label) return
		// Dedupe by id when there is one — the same brand tagged on the card
		// and on three of its products is one brand, not four.
		const key = (typeof b === 'object' && b?.id_brand != null)
			? `id:${b.id_brand}` : `name:${label.toLowerCase()}`
		if (seen.has(key)) return
		seen.add(key)
		out.push(label)
	}
	for (const b of tagging?.brands || []) push(b)
	for (const p of tagging?.products || []) for (const b of p?.brands || []) push(b)
	return out
}

function allTaggedCategories(tagging) {
	const out = []
	const seen = new Set()
	const push = (c) => {
		if (!c || seen.has(c)) return
		seen.add(c)
		out.push(c)
	}
	for (const c of tagging?.categories || []) push(c)
	for (const p of tagging?.products || []) for (const c of p?.categories || []) push(c)
	return out
}


function BrandFacetRow({ onToggle }) {
	const facets   = brandFacets.value
	const selected = selectedBrands.value
	const names    = brandNames.value

	// UNKNOWN BRAND is pinned first and kept out of the ranking. By count it
	// would top the row (5.257 resources on retail) and push a real brand out
	// of the visible slots, but it is not a brand — it is the queue of
	// resources whose brand was not recognised, and the operator filters by it
	// to go and fix them. Never hidden behind the overflow.
	const unknown = facets.find(f => f.id_brand === UNKNOWN_BRAND_ID)
	const ranked  = facets.filter(f => f.id_brand !== UNKNOWN_BRAND_ID)

	// Active chips stay visible even when they fall outside the top slots,
	// otherwise clicking a brand from the overflow would make it disappear.
	const shown = ranked.slice(0, BRAND_CHIP_SLOTS)
	const shownIds = new Set(shown.map(f => f.id_brand))
	const pinned = selected
		.filter(id => id !== UNKNOWN_BRAND_ID && !shownIds.has(id))
		.map(id => ranked.find(f => f.id_brand === id)
			|| { id_brand: id, name: names[id] || `#${id}`, n: 0 })

	const overflow = ranked.slice(BRAND_CHIP_SLOTS)
		.filter(f => !selected.includes(f.id_brand))
	const needle = brandFilter.value.trim().toLowerCase()
	const overflowMatches = needle
		? overflow.filter(f => (f.name || '').toLowerCase().includes(needle))
		: overflow

	if (!facets.length && !selected.length) return null

	const chipFor = (f, color) => {
		const active = selected.includes(f.id_brand)
		return <Chip
			key={f.id_brand}
			label={f.n > 0 ? `${f.name} · ${f.n}` : f.name}
			size="small"
			clickable
			onClick={() => onToggle(f.id_brand)}
			sx={{
				height: 26,
				fontWeight: 600,
				border: '1.5px solid',
				borderColor: color.bg,
				bgcolor: active ? color.bg : 'transparent',
				color:   active ? color.fg : color.bg,
				'&:hover': { bgcolor: active ? color.bg : `${color.bg}22` },
			}}
		/>
	}

	return <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.6 }}>
		<Typography variant="caption" sx={{
			minWidth: 78, color: 'text.secondary',
			textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
		}}>
			Brands
		</Typography>
		{unknown && chipFor(unknown, UNKNOWN_COLOR)}
		{pinned.map(f => chipFor(f, BRAND_COLOR))}
		{shown.map(f => chipFor(f, BRAND_COLOR))}
		{overflow.length > 0 && (
			<Chip
				label={brandsExpanded.value ? 'close' : `+${overflow.length} more`}
				size="small"
				clickable
				onClick={() => { brandsExpanded.value = !brandsExpanded.value }}
				sx={{
					height: 26, fontWeight: 600,
					border: '1.5px dashed', borderColor: 'text.disabled',
					color: 'text.secondary', bgcolor: 'transparent',
				}}
			/>
		)}
		{brandsUnresolved.value > 0 && (
			<Tooltip title={
				'Resources carrying at least one brand the matcher could not identify. '
				+ 'Only counted on resources already reviewed by the AI, so the real '
				+ 'figure is higher — hence the "at least".'
			}>
				<Chip
					label={`≥ ${brandsUnresolved.value} unresolved`}
					size="small"
					sx={{
						height: 26, fontWeight: 600, border: '1.5px dotted',
						borderColor: 'text.disabled', color: 'text.secondary',
						bgcolor: 'transparent',
					}}
				/>
			</Tooltip>
		)}
		{brandsExpanded.value && (
			<Box sx={{ width: '100%', mt: 0.8, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
				<TextField
					size="small"
					fullWidth
					autoFocus
					placeholder="Search brand…"
					value={brandFilter.value}
					onChange={e => { brandFilter.value = e.target.value }}
					InputProps={{ startAdornment: (
						<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
					) }}
					sx={{ mb: 1 }}
				/>
				<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.6, maxHeight: 220, overflowY: 'auto' }}>
					{overflowMatches.map(f => chipFor(f, BRAND_COLOR))}
					{overflowMatches.length === 0 && (
						<Typography variant="caption" sx={{ color: 'text.secondary' }}>
							No brand matches “{brandFilter.value}” in the current filters.
						</Typography>
					)}
				</Stack>
			</Box>
		)}
	</Stack>
}


function ChipFilterRow({ label, slots, selected, styleFor, onToggle }) {
	return <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.6 }}>
		<Typography variant="caption" sx={{
			minWidth: 78, color: 'text.secondary',
			textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600,
		}}>
			{label}
		</Typography>
		{slots.map(s => {
			const sty = styleFor(s)
			if (!sty) return null
			const active = selected.includes(s)
			return <Chip
				key={s}
				label={sty.label}
				size="small"
				clickable
				onClick={() => onToggle(s)}
				sx={{
					height: 26,
					fontWeight: 600,
					border: '1.5px solid',
					borderColor: sty.bg,
					bgcolor: active ? sty.bg : 'transparent',
					color:   active ? sty.fg : sty.bg,
					'&:hover': { bgcolor: active ? sty.bg : `${sty.bg}22` },
				}}
			/>
		})}
	</Stack>
}


function HeaderStats() {
	// Inline strip between filters and grid: total count on the left,
	// QA filter chips (clickable to slice the grid), and a small
	// min-seen input on the right. Keeps the busy filter Paper above
	// uncluttered while still surfacing the secondary tools.
	const { needs_qa, validated } = counts.value
	return <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
		<Typography variant="body2" sx={{ color: 'text.secondary' }}>
			<strong style={{ color: 'inherit' }}>{total.value}</strong> resources
		</Typography>
		<Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
		<Stack direction="row" spacing={0.6}>
			<Chip size="small" color="error" variant="outlined"
				label={`${needs_qa} need QA`}
				clickable
				onClick={() => { selectedNeedsQa.value = selectedNeedsQa.value === true ? null : true; page.value = 1 }}
				sx={{ borderWidth: selectedNeedsQa.value === true ? 2 : 1 }}
			/>
			<Chip size="small" color="success" variant="outlined"
				label={`${validated} validated`}
				clickable
				onClick={() => { selectedNeedsQa.value = selectedNeedsQa.value === false ? null : false; page.value = 1 }}
				sx={{ borderWidth: selectedNeedsQa.value === false ? 2 : 1 }}
			/>
		</Stack>
		<Box sx={{ flex: 1 }} />
		<TextField size="small" type="number" label="Min seen"
			value={minCountSeen.value}
			onChange={e => { minCountSeen.value = e.target.value; page.value = 1 }}
			InputLabelProps={{ shrink: true }}
			inputProps={{ min: 0 }}
			sx={{ width: 110 }}
		/>
	</Stack>
}


function ResourcesGrid({ openDetail }) {
	// Auto-fill responsive grid: 4-5 cards on a 1440px monitor, 2-3 on a
	// 1024 laptop, single column on phones. Card width is anchored to the
	// thumb's natural width range so the artwork stays scannable.
	return <Box sx={{
		mt: 0,
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
		gap: 2,
	}}>
		{items.value.map(r => (
			<ResourceCard key={r.id_resource} r={r} openDetail={openDetail} />
		))}
	</Box>
}


function ResourceCard({ r, openDetail }) {
	const thumbSrc = r.crop_inlined || null
	const tagging  = r.tagging_json || {}
	const brands = allTaggedBrands(tagging)
	const cats = allTaggedCategories(tagging)
	// Sections this creative has shown up in, sorted by recency. We
	// render the full list (each entry is a router-link into Banners
	// Review for that section + date) rather than collapsing — the
	// dashboard's whole point is seeing the spread at a glance.
	const sections = r.sections_seen || []
	const isMerge = mergeMode.value
	const isSelected = isMerge && mergeSelection.value.has(r.id_resource)
	const handleClick = () => {
		if (!isMerge) {
			openDetail(r.id_resource)
			return
		}
		// In merge mode, the whole card is a toggle. Mutate a fresh
		// Set so the signal sees an identity change and re-renders.
		const next = new Set(mergeSelection.value)
		if (next.has(r.id_resource)) next.delete(r.id_resource)
		else next.add(r.id_resource)
		mergeSelection.value = next
	}
	return <Paper
		variant="outlined"
		onClick={handleClick}
		sx={{
			cursor: 'pointer', overflow: 'hidden',
			display: 'flex', flexDirection: 'column', position: 'relative',
			transition: 'transform 120ms, box-shadow 120ms, border-color 120ms',
			borderColor: isSelected ? 'primary.main' : (r.ignored ? 'grey.300' : 'divider'),
			borderWidth: isSelected ? 2 : 1,
			bgcolor: isSelected ? 'primary.50' : 'background.paper',
			opacity: r.ignored ? 0.65 : 1,
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: 3,
				borderColor: 'primary.main',
			},
		}}
	>
		{/* Selection checkbox in merge mode — sits at top-left of the
		    card, above the thumb. Clicking anywhere on the card toggles;
		    the checkbox just communicates state. */}
		{isMerge && (
			<Checkbox
				size="small"
				checked={isSelected}
				onChange={handleClick}
				onClick={e => e.stopPropagation()}
				sx={{
					position: 'absolute', top: 4, left: 4, zIndex: 2,
					bgcolor: 'background.paper',
					'&:hover': { bgcolor: 'background.paper' },
					p: 0.5,
				}}
			/>
		)}
		{/* Thumb area — fixed aspect 4:3 to keep the grid aligned even when
		    crops have wildly different ratios. object-fit:contain inside so
		    aspect is preserved without cropping. */}
		<Box sx={{
			position: 'relative',
			width: '100%',
			aspectRatio: '4 / 3',
			bgcolor: 'grey.50',
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			overflow: 'hidden',
		}}>
			{thumbSrc ? (
				<Box component="img" src={thumbSrc} alt=""
					sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
				/>
			) : (
				<Typography variant="caption" sx={{ color: 'text.disabled' }}>no crop</Typography>
			)}
			{/* Top-right: count badge — only when seen >1 since 1 is the
			    "boring" base case; we want repeats to pop. */}
			{r.count_seen > 1 && (
				<Tooltip title={`Seen ${r.count_seen} times across captures`}>
					<Chip
						size="small"
						label={`×${r.count_seen}`}
						color="primary"
						sx={{
							position: 'absolute', top: 6, right: 6,
							fontWeight: 700, fontSize: 12, height: 22,
						}}
					/>
				</Tooltip>
			)}
			{/* Top-left: state markers — small icons so they don't shout. */}
			<Stack direction="row" spacing={0.4} sx={{ position: 'absolute', top: 6, left: 6 }}>
				{r.ignored && (
					<Tooltip title="Ignored — excluded from matching">
						<Chip
							size="small"
							icon={<VisibilityOffIcon style={{ fontSize: 12 }} />}
							label="ignored"
							sx={{ height: 22, fontSize: 11, bgcolor: 'grey.700', color: 'common.white' }}
						/>
					</Tooltip>
				)}
				{r.needs_qa ? (
					<Tooltip title={r.qa_reason || 'Needs human QA'}>
						<Chip
							size="small"
							icon={<WarningAmberIcon style={{ fontSize: 12, color: 'inherit' }} />}
							label="QA"
							sx={{
								height: 22, fontSize: 11,
								bgcolor: 'error.main', color: 'common.white',
								'& .MuiChip-icon': { color: 'common.white' },
							}}
						/>
					</Tooltip>
				) : (
					<Tooltip title={r.reviewed_by ? `Validated by ${r.reviewed_by}` : 'Validated'}>
						<Chip
							size="small"
							icon={<CheckCircleIcon style={{ fontSize: 12, color: 'inherit' }} />}
							label="OK"
							sx={{
								height: 22, fontSize: 11,
								bgcolor: 'success.main', color: 'common.white',
								'& .MuiChip-icon': { color: 'common.white' },
							}}
						/>
					</Tooltip>
				)}
			</Stack>
			{/* Bottom corners mirror the filter axes: Media Type on the
			    left, Intent on the right. Each chip wears the same colour
			    as the filter chip the user would click to find this card,
			    so the visual link is immediate. Intent only renders when
			    set — product-list and brand-logos resources legitimately
			    have no intent and we don't want to fake one. */}
			{(() => {
				// The family is the IDENTITY. What this chip showed before was
				// the last occurrence's parent_kind, which says where the
				// creative appeared, not what it is — and the same product
				// shows up in a product-list and in a product-slider, so that
				// can no longer headline the card.
				const famSty = familyStyle(r.family_label)
				// Intent only means something for a product when the sighting
				// came from a product-slider: a slider slide is a creative
				// with a purpose, a product-list cell is just a listing row.
				// Curro, 2026-09-03. Other families keep it unconditionally.
				//
				// Read from `kinds_seen`, not from the resource: where a
				// sighting appeared is per-occurrence data, and 31 resources
				// in douglas_nl show up in BOTH containers — for those the
				// resource-level intent cannot be right for both, which is why
				// `intent` belongs on the occurrence (Tech Enabler pending).
				const esProducto = r.family_label === 'product'
				const deSlider = (r.kinds_seen || []).some(
					k => k.parent_kind === 'product-slider')
				const itSty = (r.intent && (!esProducto || deSlider))
					? intentStyle(r.intent) : null
				return <>
					<Chip
						size="small"
						label={r.family_mixed ? 'MIXED' : famSty.label}
						title={r.family_mixed
							? `Breaks R1 — spans: ${(r.families_seen || []).join(' + ')}`
							: undefined}
						sx={{
							position: 'absolute', bottom: 6, left: 6,
							height: 22, fontSize: 11, fontWeight: 600,
							bgcolor: famSty.bg, color: famSty.fg,
							'& .MuiChip-label': { px: 0.9 },
						}}
					/>
					{itSty && <Chip
						size="small"
						label={itSty.label}
						sx={{
							position: 'absolute', bottom: 6, right: 6,
							height: 22, fontSize: 11, fontWeight: 600,
							bgcolor: itSty.bg, color: itSty.fg,
							'& .MuiChip-label': { px: 0.9 },
						}}
					/>}
				</>
			})()}
		</Box>

		{/* Body — flat list of the resource's identifying data. Intent /
		    eyecatch / sponsored / type already live in the thumb chips
		    (or simply don't belong on the card), so we keep this block
		    focused on Site, Sections, Brands, Categories, and visibility
		    span. */}
		<Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 100 }}>
			<Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={0.5}>
				<Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{r.retailer.slug}
				</Typography>
				<Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: 10 }}>
					#{r.id_resource}
				</Typography>
			</Stack>

			{/* Every section the resource has been seen in. Each one is a
			    deep-link into Banners Review pre-loaded for that section
			    on the date of its last occurrence — quickest way for the
			    operator to verify context. stopPropagation so the click
			    doesn't open the detail drawer underneath. */}
			<CardLabelRow label="Section">
				{sections.length === 0 ? (
					<Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
				) : sections.map((s, i) => {
					const params = new URLSearchParams({
						id_retailer: String(r.retailer.id_retailer),
						id_section:  String(s.id_section),
					})
					if (s.last_seen_in_section) {
						params.set('date', s.last_seen_in_section.slice(0, 10))
					}
					const href = `${VISIBILITY_TRACKER_ROUTES.REVIEW_PATH}?${params.toString()}`
					return <React.Fragment key={s.id_section}>
						{i > 0 && <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>, </Typography>}
						<Link
							component={RouterLink}
							to={href}
							onClick={e => e.stopPropagation()}
							variant="caption"
							sx={{ fontSize: 11 }}
						>
							{s.slug}
						</Link>
					</React.Fragment>
				})}
			</CardLabelRow>

			{/* Operator who ran the task that FIRST created this resource.
			    Handy for quick audit ("who tagged this originally") without
			    opening the drawer and scrolling through occurrences. */}
			{r.origin_user && (
				<CardLabelRow label="Captured by">
					<Typography variant="caption" sx={{ fontSize: 11 }}>
						{r.origin_user.email
							? r.origin_user.email.split('@')[0]
							: `user #${r.origin_user.id_user}`}
					</Typography>
				</CardLabelRow>
			)}

			{r.composition && (() => {
				const sty = compositionStyle(r.composition)
				if (!sty) return null
				return <CardLabelRow label="Comp">
					<Chip size="small"
						label={sty.label}
						sx={{
							height: 18, fontSize: 10, fontWeight: 600,
							bgcolor: sty.bg, color: sty.fg,
							'& .MuiChip-label': { px: 0.7 },
						}}
					/>
				</CardLabelRow>
			})()}

			{brands.length > 0 && (
				<CardChipRow label="Brands" items={brands} color="secondary" />
			)}
			{cats.length > 0 && (
				<CardChipRow label="Cat"    items={cats}   color="info" />
			)}

			{/* Visibility span: first / last timestamps + how many calendar
			    days separate them (inclusive — same-day resources count as
			    1). Gives the operator a sense of campaign duration. */}
			<Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 'auto', pt: 0.3, flexWrap: 'wrap' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
					first {formatTs(r.first_seen_at)} · last {formatTs(r.last_seen_at)}
				</Typography>
				{(() => {
					const days = daysAvailable(r.first_seen_at, r.last_seen_at)
					if (days === null) return null
					return <Chip size="small" variant="outlined"
						label={`${days} day${days === 1 ? '' : 's'}`}
						sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.6 } }}
					/>
				})()}
			</Stack>
		</Box>
	</Paper>
}


function CardChipRow({ label, items: arr, color }) {
	// Renders ALL items as chips (no +N collapse) — the dashboard's
	// detail-on-glance value comes from seeing the full brand/category
	// list without opening the drawer.
	return <Stack direction="row" spacing={0.3} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.3 }}>
		<Typography variant="caption" sx={{ minWidth: 48, color: 'text.disabled', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3 }}>
			{label}
		</Typography>
		{arr.map((s, i) => (
			<Chip key={i} size="small" label={s} variant="outlined" color={color}
				sx={{ height: 18, fontSize: 10 }}
			/>
		))}
	</Stack>
}


// Same label column as CardChipRow but accepts arbitrary children
// (text, Links, etc.) instead of chip-ifying a string array. Used for
// the Section row where each entry is a clickable router link, not a
// chip.
function CardLabelRow({ label, children }) {
	return <Stack direction="row" spacing={0.3} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.3 }}>
		<Typography variant="caption" sx={{ minWidth: 48, color: 'text.disabled', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3 }}>
			{label}
		</Typography>
		{children}
	</Stack>
}


// Inclusive day span between two ISO timestamps. Same-day → 1; null on
// missing input. Used for the "N days" chip on each card so the
// operator can see at a glance how long a creative has been live.
function daysAvailable(firstISO, lastISO) {
	if (!firstISO || !lastISO) return null
	const f = new Date(firstISO); const l = new Date(lastISO)
	if (isNaN(f.getTime()) || isNaN(l.getTime())) return null
	const ms = l - f
	if (ms < 0) return 1
	return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
}


// Reusable resource thumbnail. Uses object-fit:contain so the original
// aspect ratio is preserved — banners are often very wide and a
// cover-fit would chop them. The container reserves a fixed square box
// so rows align vertically; the image sits centred inside.
function ResourceThumb({ src, maxSize = 100, onClick }) {
	const box = {
		width: maxSize, height: maxSize,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		bgcolor: 'grey.50',
		border: '1px solid', borderColor: 'divider',
		borderRadius: 0.75,
		overflow: 'hidden',
		cursor: onClick ? 'pointer' : 'default',
		transition: 'border-color 120ms',
		'&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
	}
	if (!src) {
		return <Box sx={box} onClick={onClick}>
			<Typography variant="caption" sx={{ color: 'text.disabled' }}>no crop</Typography>
		</Box>
	}
	return <Box sx={box} onClick={onClick}>
		<Box component="img" src={src} alt=""
			sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
		/>
	</Box>
}


// Brands + Categories rendered as separate, labelled lines. The list
// version (`compact`) truncates each list to a few chips with a "+N"
// overflow marker; the drawer version (`compact=false`) shows every chip.
function TaggingFields({ tagging, compact = false }) {
	const t = tagging || {}
	const brands = allTaggedBrands(t)
	const cats = allTaggedCategories(t)
	const dest = t.destination_url

	const Section = ({ label, items: arr, color = 'primary', max = compact ? 3 : 100 }) => {
		if (!arr || !arr.length) return null
		const shown = arr.slice(0, max)
		const extra = arr.length - shown.length
		return <Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
			<Typography variant="caption" sx={{ minWidth: 50, color: 'text.disabled', textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.3 }}>
				{label}
			</Typography>
			{shown.map((s, i) => (
				<Chip key={i} size="small" label={s} variant="outlined" color={color}
					sx={{ fontSize: 11, height: 20 }}
				/>
			))}
			{extra > 0 && (
				<Tooltip title={arr.slice(max).join(', ')}>
					<Chip size="small" label={`+${extra}`} variant="outlined" sx={{ fontSize: 10, height: 20 }} />
				</Tooltip>
			)}
		</Stack>
	}

	const hasAny = brands.length || cats.length || dest
	if (!hasAny) {
		return <Typography variant="caption" sx={{ color: 'text.disabled' }}>(no tagging)</Typography>
	}
	return <Stack spacing={0.1}>
		<Section label="Brands"     items={brands} color="secondary" />
		<Section label="Categories" items={cats}   color="info" />
		{dest && (
			<Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexWrap: 'wrap', mt: 0.3 }}>
				<Typography variant="caption" sx={{ minWidth: 50, color: 'text.disabled', textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.3 }}>
					Link
				</Typography>
				<Link href={dest} target="_blank" rel="noopener noreferrer"
					variant="caption" underline="hover"
					sx={{ fontSize: 11, wordBreak: 'break-all' }}
				>
					{dest.length > 60 ? dest.slice(0, 60) + '…' : dest}
				</Link>
			</Stack>
		)}
	</Stack>
}


// Smaller column for the leaf's role-dependent tagging: type +
// eyecatch / sponsored flags + product counts. Visually distinct from
// brands/categories so the eye can scan it independently.
function TypeFlags({ tagging }) {
	const t = tagging || {}
	const items = []
	if (t.type) items.push({ label: 'type', value: t.type, color: 'primary' })
	// No ★ / $ chips: a resource-level "sponsored" claims a fact about a
	// SIGHTING and paints it over every one of them (869ehankw). Both
	// live on VT_RESOURCE_OCCURRENCES now, and migration 042 removes the
	// keys from the resource entirely — these chips would go blank on
	// their own, so they leave with the reason rather than by accident.
	if (Array.isArray(t.products) && t.products.length) {
		items.push({ label: 'products', value: `${t.products.length}`, color: 'info' })
	} else if (t.product) {
		items.push({ label: 'product', value: '1', color: 'info' })
	}
	if (!items.length) {
		return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
	}
	return <Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3 }}>
		{items.map((it, i) => (
			<Tooltip key={i} title={it.label}>
				<Chip size="small" label={it.value} color={it.color} variant="outlined"
					sx={{ fontSize: 11, height: 22 }}
				/>
			</Tooltip>
		))}
	</Stack>
}


function Pagination() {
	const totalPages = Math.max(1, Math.ceil(total.value / limit.value))
	if (totalPages <= 1) return null
	return <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
		<Typography variant="caption">Page {page.value} / {totalPages}</Typography>
		<Button size="small" disabled={page.value <= 1}
			onClick={() => { page.value = page.value - 1 }}>Previous</Button>
		<Button size="small" disabled={page.value >= totalPages}
			onClick={() => { page.value = page.value + 1 }}>Next</Button>
	</Stack>
}


// Tagging section of the resource detail drawer. Read-only by default;
// click "Edit" to flip into the same chip-based form ReviewQueue uses,
// validate at the resource grain through the back's fan-out worker.
function ResourceTaggingSection({ r }) {
	const [editing, setEditing] = useState(false)
	if (editing) {
		return <ResourceEditor
			r={r}
			onCancel={() => setEditing(false)}
			onSaved={() => setEditing(false)}
		/>
	}
	return <Box>
		<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
			<Typography variant="subtitle2">Tagging</Typography>
			{!r.ignored && (
				<Button size="small" startIcon={<EditIcon fontSize="small" />}
					onClick={() => setEditing(true)}>Edit</Button>
			)}
		</Stack>
		<Paper variant="outlined" sx={{ p: 1.5 }}>
			<TaggingFields tagging={r.tagging_json} />
			<Box sx={{ mt: 1.5 }}>
				<TypeFlags tagging={r.tagging_json} />
			</Box>
			{r.tagging_json && Object.keys(r.tagging_json).length > 0 && (
				<details style={{ marginTop: 12 }}>
					<summary style={{ fontSize: 11, color: '#888', cursor: 'pointer' }}>raw JSON</summary>
					<Box component="pre" sx={{ m: 0, mt: 1, p: 1, fontSize: 11, whiteSpace: 'pre-wrap', bgcolor: 'grey.50', borderRadius: 0.5 }}>
						{JSON.stringify(r.tagging_json, null, 2)}
					</Box>
				</details>
			)}
		</Paper>
	</Box>
}


// Editable form for a resource's tagging. Mirrors the ReviewQueue
// ItemCard's edit surface (same chip vocab, same brand autocomplete,
// same composition-by-category rule, same products editor). Saves
// through `/admin/review-queue/validate-resource` — the same endpoint
// the ReviewQueue uses — so the back's fan-out worker propagates the
// verdict DOWN to every occurrence and clears needs_qa across N tasks.
function ResourceEditor({ r, onCancel, onSaved }) {
	const { showToast } = useAppToast()
	const tagging = r.tagging_json || {}
	const parentKind = r.parent_kind || 'banner'
	const leafKind   = r.kind || parentKind
	const isIntentKind     = INTENT_KINDS.has(parentKind)
	// 869ehb2zd — by LEAF kind (`r.kind`, already resolved into `leafKind`
	// above), not by container. A resource whose occurrences are post-cutover
	// products is a single-product resource even though it came from a
	// carousel — and since R12 dissolved, one resource can legitimately hold
	// products seen in a grid AND in a carousel.
	const isMultiProduct   = leafKind === 'product-slide'
		&& Array.isArray(tagging.products) && tagging.products.length > 0
	const isSingleProductCard = leafKind === 'product'

	const [draft, setDraft] = useState(() => ({
		intent:        tagging.intent || '',
		composition:   tagging.composition || '',
		product_count: tagging.product_count ?? null,
		line_relation: tagging.line_relation ?? 'unknown',
		categories:    Array.isArray(tagging.categories) ? [...tagging.categories] : [],
		brands:        _sanitiseBrands(tagging.brands),
		// No eyecatch / sponsored: they are per-sighting since 869ehankw.
		product:       tagging.product || '',
		products:      Array.isArray(tagging.products) ? JSON.parse(JSON.stringify(tagging.products)) : [],
		notes:         tagging.notes || '',
		destination_url: tagging.destination_url || '',
	}))
	const [busy, setBusy] = useState(false)

	// Synthesise an ai_review shape from qa_reason so UnmatchedBrandChips
	// renders clickable chips (operator can map raw strings → canonical
	// brands here too, identical to the ReviewQueue popover flow).
	const aiReview = React.useMemo(() => ({
		needs_qa: !!r.needs_qa,
		qa_reason: r.qa_reason || '',
		unmatched_brands: _parseUnmatchedFromQaReason(r.qa_reason || ''),
	}), [r.needs_qa, r.qa_reason])

	useEffect(() => { ensureBrandCatalogue().catch(() => {}) }, [])

	// Composition vocabulary depends on draft.categories — clear an
	// out-of-vocab composition pick the moment the operator edits
	// categories such that it no longer applies.
	useEffect(() => {
		if (!draft.composition) return
		const allowed = allowedCompositions(draft.categories).map(o => o.value)
		if (!allowed.includes(draft.composition)) {
			setDraft(d => ({ ...d, composition: '' }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(draft.categories || [])])

	const save = async () => {
		setBusy(true)
		try {
			// Same per-product scrub the ReviewQueue does before posting:
			// if the operator mapped a brand, clear the AI's stale
			// unmatched_brands on that product.
			const products = (draft.products || []).map(p => {
				if (!p || !Array.isArray(p.brands) || p.brands.length === 0) return p
				const ai = p.ai_review || {}
				return {
					...p,
					ai_review: { ...ai, unmatched_brands: [], needs_qa: false, qa_reason: '' },
				}
			})
			const patch = {
				intent:      draft.intent,
				composition: draft.composition,
				categories:  draft.categories,
				brands:      draft.brands,
				// eyecatch / sponsored deliberately absent — 869ehankw.
				// The resource endpoint drops them, so sending them would
				// only produce a log line saying they were ignored.
				product:     draft.product,
				products,
				notes:       draft.notes,
				destination_url: draft.destination_url,
			}
			const res = await api.post(
				`${BACKEND_APIS.VT}/admin/review-queue/validate-resource`,
				{ id_resource: r.id_resource, patch },
			)
			if (!res.ok) {
				showToast.error('Save failed: ' + res.error)
				return
			}
			// One transaction, no propagation to wait for: the back
			// stamps every sighting of the resource together with the
			// verdict (the per-occurrence fan-out died in Phase 3 of the
			// storage redesign).
			const nTasks = res.data?.tasks_refreshed
			showToast.success(
				nTasks
					? `Saved. ${nTasks} task${nTasks === 1 ? '' : 's'} refreshed.`
					: 'Saved.'
			)
			// Refresh the resource detail so the drawer reflects the
			// freshly-validated state (kinds_seen unchanged but
			// needs_qa flips and reviewed_at/by are stamped). Don't
			// close the drawer — the operator may
			// want to keep looking at the occurrences.
			const refreshed = await api.get(`${BACKEND_APIS.VT}/admin/resources/${r.id_resource}`)
			if (refreshed.ok) detailResource.value = refreshed.data
			onSaved && onSaved()
		} finally {
			setBusy(false)
		}
	}

	const compositionOptions = allowedCompositions(draft.categories)
	const isFragOnly = isFragranceOnly(draft.categories)

	return <Box>
		<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
			<Typography variant="subtitle2">Tagging — editing</Typography>
			<Stack direction="row" spacing={1}>
				<Button size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
				<Button size="small" variant="contained" startIcon={<SaveIcon fontSize="small" />}
					onClick={save} disabled={busy}>Save</Button>
			</Stack>
		</Stack>
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				{aiReview.unmatched_brands.length > 0 && (
					<UnmatchedBrandChips
						aiReview={aiReview}
						onResolve={(raw, canonical) => {
							// Mirror the ReviewQueue resolve flow: drop the
							// raw chip + drop it from the unmatched list,
							// and append the canonical to draft.brands so
							// the next save persists it.
							_resolveUnmatched(aiReview, raw, canonical)
							setDraft(d => {
								const next = [...(d.brands || [])]
								if (!next.some(b => b?.id_brand === canonical.id_brand)) {
									next.push({ id_brand: canonical.id_brand, name: canonical.name })
								}
								return { ...d, brands: next }
							})
						}}
					/>
				)}

				{isIntentKind && (
					<Box>
						<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.06, color: 'text.secondary', fontWeight: 700 }}>
							Intent
						</Typography>
						<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
							{editorIntentChoices(draft.intent).map(s => (
								<Chip key={s} label={s} size="small"
									color={draft.intent === s ? 'primary' : 'default'}
									variant={draft.intent === s ? 'filled' : 'outlined'}
									onClick={() => setDraft({ ...draft, intent: s })}
									sx={{ cursor: 'pointer' }}
								/>
							))}
						</Stack>
					</Box>
				)}

				{isIntentKind && (
					<Box>
						<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.06, color: 'text.secondary', fontWeight: 700 }}>
							Composition
						</Typography>
						<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
							{compositionOptions.map(o => (
								<Chip key={o.value} label={o.label} size="small"
									color={draft.composition === o.value ? 'primary' : 'default'}
									variant={draft.composition === o.value ? 'filled' : 'outlined'}
									onClick={() => setDraft({
										...draft,
										composition: draft.composition === o.value ? '' : o.value,
									})}
									sx={{ cursor: 'pointer' }}
								/>
							))}
							{draft.composition && (
								<Chip label="Clear" size="small" variant="outlined"
									onClick={() => setDraft({ ...draft, composition: '' })}
									sx={{ cursor: 'pointer', color: 'text.secondary' }}
								/>
							)}
						</Stack>
						{!isFragOnly && (
							<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
								Line-similarity values (monoline / multiline / masterbrand) apply only when categories is exactly <code>fragrances</code>.
							</Typography>
						)}
					</Box>
				)}

				{isMultiProduct ? (
					<ProductsEditor
						products={draft.products}
						onChange={v => setDraft({ ...draft, products: v })}
					/>
				) : (
					<>
						<EditBrands
							value={draft.brands}
							onChange={v => setDraft({ ...draft, brands: v })}
						/>
						<EditCategories
							value={draft.categories}
							onChange={v => setDraft({ ...draft, categories: v })}
						/>
					</>
				)}

				{/* Eye-catch and Sponsored are NOT edited here any more
				    (task 869ehankw). They describe a SIGHTING, not the
				    creative: the same image runs organic one day and
				    sponsored the next, and the promo band a consolidator
				    merge glossed over must not be published for a sighting
				    that never showed it. Edit them where a single sighting
				    is in front of you — Review Queue or Banners Review. The
				    back-end already ignores them on this endpoint and says
				    so in its log. No capability is lost: these controls
				    were gated to `product-list`, so there was never a way
				    to flag a banner as sponsored from here. */}
				{isSingleProductCard && (
					<TextField size="small" label="Product name"
						value={draft.product || ''}
						onChange={e => setDraft({ ...draft, product: e.target.value })}
					/>
				)}

				<TextField size="small" label="Destination URL"
					value={draft.destination_url}
					onChange={e => setDraft({ ...draft, destination_url: e.target.value })}
				/>
			</Stack>
		</Paper>
	</Box>
}


function DetailDrawer() {
	const open = Boolean(detailResource.value) || detailLoading.value
	const close = () => { detailResource.value = null }
	return <Drawer anchor="right" open={open} onClose={close}
		PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}
	>
		<Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
			<Typography variant="h6">Resource detail</Typography>
			<IconButton onClick={close}><CloseIcon /></IconButton>
		</Box>
		{detailLoading.value ? (
			<Box sx={{ p: 3 }}>
				<CircularProgress size={22} />
			</Box>
		) : detailResource.value ? (
			<DetailBody r={detailResource.value} />
		) : null}
	</Drawer>
}


function DetailBody({ r }) {
	const mainSrc = r.crop_inlined || null
	const sections = r.sections_seen || []
	// Modal state lives local to the drawer — opening a viewer or a
	// collage from a different resource shouldn't bleed across detail
	// loads. `lightboxSrc` is null when closed, otherwise the data: URI
	// to render full-size. `collageOpen` toggles the side-by-side grid.
	const [lightboxSrc, setLightboxSrc] = useState(null)
	const [collageOpen, setCollageOpen] = useState(false)
	// Provenance (back 1.87.0): why each sighting is here, and what the
	// resource itself absorbed. Loaded separately so the drawer paints
	// immediately and the audit fills in — it can be ~1k events on an old
	// resource and it is never the reason to wait for the crops.
	const [linkage, setLinkage] = useState(null)
	useEffect(() => {
		let alive = true
		setLinkage(null)
		api.get(`${BACKEND_APIS.VT}/admin/resources/${r.id_resource}/linkage`)
			.then(res => { if (alive && res.ok) setLinkage(res.data) })
			.catch(() => {})
		return () => { alive = false }
	}, [r.id_resource])
	// Latest event per sighting: what put it where it is now.
	const linkageByOcc = {}
	for (const e of (linkage?.events || [])) {
		if (e.id_occurrence) linkageByOcc[e.id_occurrence] = e
	}
	const mergeEvents = (linkage?.events || []).filter(e => e.event_type === 'merge')
	// All image entries for the collage: one per occurrence. We DON'T
	// add the master crop as a separate entry — it's the same image as
	// the origin occurrence, and adding it would always inflate the
	// collage count by one ("Compare all (2)" on a resource with a single
	// occurrence is misleading: the two images are identical). The
	// master is already visible at the top of the drawer; if the user
	// wants to compare, the collage shows the occurrence list as-is.
	const collageItems = (r.occurrences || []).map(o => ({
		key: `occ-${o.id_occurrence}`,
		src: o.crop_inlined || null,
		label: o.hamming_distance === 0 ? 'exact' : `dist ${o.hamming_distance}`,
		meta: `task #${o.id_task}${o.section ? ' · ' + o.section.slug : ''} · ${formatTs(o.seen_at)}`,
	})).filter(it => it.src)
	return <Box sx={{ px: 2, pb: 4 }}>
		<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
			<ResourceThumb src={mainSrc} maxSize={280} onClick={() => setLightboxSrc(mainSrc)} />
			<Stack spacing={0.8} sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="body1" sx={{ fontWeight: 600 }}>
					{r.retailer.slug}
				</Typography>
				<Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
					#{r.id_resource} · pHash {r.phash}
				</Typography>
				{sections.length > 0 && (
					<Box>
						<Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.3, display: 'block' }}>
							Seen in {sections.length} section{sections.length === 1 ? '' : 's'} (most recent first)
						</Typography>
						<Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3, mt: 0.4 }}>
							{sections.map(s => (
								<Tooltip key={s.id_section} title={`Last seen ${formatTs(s.last_seen_in_section)}`}>
									<Chip size="small" variant="outlined" label={s.slug}
										sx={{ height: 20, fontSize: 11 }}
									/>
								</Tooltip>
							))}
						</Stack>
					</Box>
				)}
				<Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', gap: 0.4, alignItems: 'center' }}>
					<Chip size="small" label={r.kind} variant="outlined" />
					{r.parent_kind && r.parent_kind !== r.kind && (
						<Chip size="small" label={`in ${r.parent_kind}`} variant="outlined" />
					)}
					{/* Multi-context: the same resource has been observed in
					    more than one (kind, parent_kind) combo across its
					    occurrences. Surface so the operator knows the same
					    crop drives several roles on the page. */}
					{Array.isArray(r.kinds_seen) && r.kinds_seen.length > 1 && (
						<Tooltip title={
							'Appears in: ' +
							r.kinds_seen.map(k => {
								const donde = k.parent_kind || k.kind
								return k.n ? `${donde} ×${k.n}` : donde
							}).join(' · ')
						}>
							<Chip
								size="small"
								color="info"
								variant="outlined"
								/* The split, not just how many places: a product
								   seen 45 times in a product-list and 3 in a
								   product-slider is not the same as one evenly
								   spread, and where it appears is per-occurrence
								   data now, not a property of the resource. */
								label={r.kinds_seen.every(k => k.n)
									? r.kinds_seen.map(k =>
										`${(k.parent_kind || k.kind)} ×${k.n}`).join('  ')
									: `${r.kinds_seen.length} places`}
							/>
						</Tooltip>
					)}
					{r.ignored && <Chip size="small" color="default" variant="outlined" label="ignored" />}
					{r.needs_qa
						? <Chip size="small" color="error" label="needs QA" />
						: <Chip size="small" color="success" variant="outlined" label="validated" />
					}
					{/* Validate-from-queue shortcut. Jumps to the resource-centric
					    Review Queue with this resource seeded at the top of the
					    batch. Only meaningful when the resource is pending. */}
					{r.needs_qa && !r.ignored && (
						<Chip
							size="small"
							clickable
							component="a"
							href={`/visibility-tracker/review-queue?seed=${r.id_resource}`}
							color="primary"
							variant="filled"
							label="Validate →"
							sx={{ fontWeight: 700 }}
						/>
					)}
				</Stack>
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					Seen <strong>{r.count_seen}</strong> time(s) ·
					first <em>{formatTs(r.first_seen_at)}</em> ·
					last <em>{formatTs(r.last_seen_at)}</em>
				</Typography>
				{r.crop_path && (
					<Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 10 }}>
						{r.crop_path}
					</Typography>
				)}
				{r.qa_reason && (
					<Alert severity="warning" variant="outlined" sx={{ py: 0.4 }}>
						<Typography variant="caption">{r.qa_reason}</Typography>
					</Alert>
				)}
			</Stack>
		</Stack>

		<Box sx={{ mt: 3 }}>
			<ResourceTaggingSection r={r} />
		</Box>

		<Box sx={{ mt: 3 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="subtitle2">
					Occurrences ({r.occurrences?.length || 0})
				</Typography>
				{collageItems.length > 1 && (
					<Button size="small" variant="outlined"
						startIcon={<ViewModuleIcon fontSize="small" />}
						onClick={() => setCollageOpen(true)}
					>
						Compare all ({collageItems.length})
					</Button>
				)}
			</Stack>
			{mergeEvents.length > 0 && <MergeHistory events={mergeEvents} />}
			<OccurrencesList occurrences={r.occurrences || []}
				linkageByOcc={linkageByOcc}
				onImageClick={src => setLightboxSrc(src)} />
		</Box>

		<ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
		<CollageDialog open={collageOpen} items={collageItems}
			onClose={() => setCollageOpen(false)}
			onImageClick={src => setLightboxSrc(src)}
		/>
	</Box>
}


function ImageLightbox({ src, onClose }) {
	// Plain fullscreen-ish modal with the inlined image at its natural
	// aspect ratio. ESC closes via MUI Dialog's default behaviour;
	// clicking outside also closes. The image itself sits centred,
	// capped at 90vw / 90vh so it always fits the viewport without
	// scrollbars.
	if (!src) return null
	return <Dialog open={Boolean(src)} onClose={onClose} maxWidth={false}
		PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.85)', m: 0, maxWidth: '95vw', maxHeight: '95vh' } }}
	>
		<DialogContent sx={{ p: 1, position: 'relative' }} onClick={onClose}>
			<IconButton onClick={onClose}
				sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', zIndex: 1 }}
			>
				<CloseIcon />
			</IconButton>
			<Box component="img" src={src} alt=""
				onClick={e => e.stopPropagation()}
				sx={{
					display: 'block',
					maxWidth: '90vw', maxHeight: '85vh',
					objectFit: 'contain',
					cursor: 'default',
				}}
			/>
		</DialogContent>
	</Dialog>
}


function CollageDialog({ open, items, onClose, onImageClick }) {
	// Side-by-side grid for comparing the master crop against every
	// occurrence crop. Tile size is generous (~280px) so the operator
	// can spot subtle creative differences (overlays, color casts,
	// product position) that pHash didn't distinguish. Click a tile to
	// open the single-image lightbox for an even bigger view.
	return <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
		<Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
			<Typography variant="h6">Collage — {items.length} crops</Typography>
			<IconButton onClick={onClose}><CloseIcon /></IconButton>
		</Box>
		<DialogContent>
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
				gap: 2,
			}}>
				{items.map(it => (
					<Paper key={it.key} variant="outlined" sx={{ p: 1 }}>
						<Box
							onClick={() => onImageClick(it.src)}
							sx={{
								width: '100%', aspectRatio: '1 / 1',
								bgcolor: 'grey.50',
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								overflow: 'hidden', cursor: 'pointer',
								border: '1px solid', borderColor: 'divider', borderRadius: 0.5,
								transition: 'border-color 120ms',
								'&:hover': { borderColor: 'primary.main' },
							}}
						>
							<Box component="img" src={it.src} alt=""
								sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
							/>
						</Box>
						<Stack direction="row" spacing={0.6} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
							<Chip size="small" variant="outlined" label={it.label}
								color={it.label === 'Origin' ? 'success' : (it.label === 'exact' ? 'primary' : 'default')}
								sx={{ height: 22, fontSize: 11 }}
							/>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>
								{it.meta}
							</Typography>
						</Stack>
					</Paper>
				))}
			</Box>
		</DialogContent>
	</Dialog>
}


// Human wording for an audit event. The rule is the interesting part —
// "merged by brand set at 32 bits" is what a bad join looks like from the
// outside, and it took a full morning of forensics to say that about
// resource 282419 before this existed.
const RULE_LABEL = {
	'phash-hit': 'emparejada por imagen',
	'phash-miss': 'creó su propio recurso',
	'tombstone-redirect': 'redirigida desde un recurso fusionado',
	'gate-demote': 'rechazada por el control visual',
	'brand-set+phash': 'fusionada por marca + imagen',
	'title-equality': 'fusionada por título idéntico',
	'cohort-median': 'fusionada por parecido visual',
	'suggested-group': 'grupo sugerido aplicado a mano',
	'manual': 'fusión manual de un operador',
	'reconstructed': 'sin registro: derivado del estado actual',
	// The resource reconstruction (869eu5kp4): each one says WHY in its
	// corroboration, which `corroborationText` turns into words.
	'capture-recluster': 'misma captura (reagrupado de media)',
	'product-brand-title': 'misma marca + título',
	'logo-brand-set': 'mismo conjunto de marcas (logos)',
	'jev-same': 'Jev: mismo producto con otro título',
	'product-title-reviewed': 'revisado a ojo: mismo producto',
	'undo-reviewed-merge': 'fusión deshecha tras la revisión',
	'ungroup-products': 'separado para recalcular por texto',
	'text-miss': 'creó su recurso por texto',
}

// The evidence a rule of the reconstruction left in `corroboration`, in the
// words of the Resources window. 30-09: Coco Mademoiselle had joined Chance
// Eau Fraîche and the window only said the rule's name; the numbers that
// explained it (ORB 0,213 against 0,20, pixels 11,8 against 12) were in the
// audit and nowhere on screen.
function corroborationText(ev) {
	const c = ev.corroboration || {}
	const n = (v, d = 3) => (typeof v === 'number' ? v.toFixed(d).replace(/\.?0+$/, '') : v)
	switch (ev.rule) {
		case 'capture-recluster': {
			const bits = []
			if (c.orb_min != null) bits.push(`ORB ${n(c.orb_min)}${c.umbral != null ? ` (mín ${n(c.umbral)})` : ''}`)
			if (c.mad_max != null) bits.push(`píxeles ${n(c.mad_max, 1)}${c.umbral_pix != null ? ` (máx ${n(c.umbral_pix, 1)})` : ''}`)
			return bits.join(' · ')
		}
		case 'jev-same': {
			const bits = []
			if (c.p_same != null) bits.push(`P(mismo) ${n(c.p_same, 2)}`)
			if (c.title_member || c.title_target) bits.push(`«${c.title_member || '?'}» ≈ «${c.title_target || '?'}»`)
			return bits.join(' · ')
		}
		case 'product-brand-title':
			return c.titulo ? `título «${c.titulo}»` : ''
		case 'logo-brand-set':
			return c.nombres ? `marcas ${c.nombres}` : ''
		default:
			return ''
	}
}

function ProvenanceLine({ ev }) {
	if (!ev) return null
	// Everything predating the audit is `reconstructed`, and the backend
	// rightly calls it weak — but that is nearly a million rows, so paint
	// it neutral. If "no record" and "merged 32 bits past its own gate"
	// look the same, the warning stops meaning anything and the operator
	// learns to ignore exactly the rows worth stopping at.
	const derived = ev.rule === 'reconstructed'
	const alarm = ev.weak && !derived
	const bits = []
	if (ev.distance_phash !== null && ev.distance_phash !== undefined) {
		const gate = ev.gate_applied ?? ev.threshold_applied
		bits.push(`${ev.distance_phash} bits${gate != null ? ` / límite ${gate}` : ''}`)
	}
	if (ev.cosine !== null && ev.cosine !== undefined) bits.push(`coseno ${ev.cosine.toFixed(3)}`)
	const evidence = corroborationText(ev)
	if (evidence) bits.push(evidence)
	if (ev.actor) bits.push(ev.actor.split('@')[0])
	return <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.4 }}>
		<Tooltip title={[ev.rule, ev.notes, alarm ? 'evidencia justa: merece un vistazo' : null]
			.filter(Boolean).join(' · ')}>
			<Chip size="small" variant={alarm ? 'filled' : 'outlined'}
				color={alarm ? 'warning' : 'default'}
				label={RULE_LABEL[ev.rule] || ev.rule}
				sx={{ height: 20, fontSize: 10, opacity: derived ? 0.6 : 1 }}
			/>
		</Tooltip>
		{bits.length > 0 && (
			<Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
				{bits.join(' · ')}
			</Typography>
		)}
	</Stack>
}

function MergeHistory({ events }) {
	return <Paper variant="outlined" sx={{ p: 1.2, mb: 1.5 }}>
		<Typography variant="caption" sx={{ fontWeight: 600 }}>
			Este recurso absorbió {events.length} recurso{events.length === 1 ? '' : 's'}
		</Typography>
		<Stack spacing={0.6} sx={{ mt: 0.8 }}>
			{events.map(e => <Stack key={e.id_event} direction="row" spacing={1}
				alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.4 }}>
				<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
					#{e.id_resource_from}
				</Typography>
				<ProvenanceLine ev={e} />
				<Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
					{formatTs(e.ts)}
				</Typography>
			</Stack>)}
		</Stack>
	</Paper>
}

function OccurrencesList({ occurrences, linkageByOcc = {}, onImageClick }) {
	if (!occurrences.length) return <Alert severity="info">No occurrences recorded yet.</Alert>
	// "Origin" is the oldest row (smallest id_occurrence) — Hamming
	// distance alone can't disambiguate origin from exact-match re-runs
	// because both store distance=0. We compute the origin id here once
	// and tag exactly that row.
	const originId = occurrences.reduce((acc, o) =>
		acc === null || o.id_occurrence < acc ? o.id_occurrence : acc, null,
	)
	return <Stack spacing={1}>
		{occurrences.map(o => {
			const occSrc = o.crop_inlined || null
			const isOrigin = o.id_occurrence === originId
			const distLabel = isOrigin ? 'origin' : (
				o.hamming_distance === 0 ? 'exact match' : `dist ${o.hamming_distance}`
			)
			const distColor = isOrigin
				? 'success'
				: (o.hamming_distance === 0 ? 'primary' : (o.hamming_distance <= 2 ? 'primary' : 'warning'))
			return <Paper key={o.id_occurrence} variant="outlined" sx={{ p: 1 }}>
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<ResourceThumb src={occSrc} maxSize={72}
						onClick={occSrc ? () => onImageClick?.(occSrc) : undefined}
					/>
					<Stack spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
						<Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.4 }}>
							<Chip size="small" variant="outlined" color={distColor} label={distLabel} />
							{o.section && (
								<Chip size="small" variant="outlined" label={o.section.slug}
									sx={{ height: 20, fontSize: 10 }}
								/>
							)}
							<Typography variant="caption">task #{o.id_task}</Typography>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>· {formatTs(o.seen_at)}</Typography>
							{o.captured_by && (
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									· by <Box component="span" sx={{ fontWeight: 600 }}>{
										o.captured_by.email
											? o.captured_by.email.split('@')[0]
											: `user #${o.captured_by.id_user}`
									}</Box>
								</Typography>
							)}
						</Stack>
						{/* What belongs to THIS sighting. Before, only distance,
						    section, task and date showed: where it appeared and
						    how it was flagged are its own data, not the
						    resource's, and the same creative can be sponsored
						    one day and not the next. */}
						<Stack direction="row" spacing={0.6} alignItems="center"
							sx={{ flexWrap: 'wrap', gap: 0.4 }}>
							{(o.parent_kind || o.leaf_kind) && (
								<Chip size="small" variant="outlined"
									sx={{ height: 20, fontSize: 10 }}
									label={o.parent_kind && o.leaf_kind && o.parent_kind !== o.leaf_kind
										? `${o.parent_kind} / ${o.leaf_kind}`
										: (o.parent_kind || o.leaf_kind)}
								/>
							)}
							{o.sponsored && (
								<Chip size="small" color="secondary"
									sx={{ height: 20, fontSize: 10 }} label="sponsored" />
							)}
							{Array.isArray(o.eyecatch) && o.eyecatch.map((e, i) => (
								<Chip key={i} size="small" variant="outlined" color="warning"
									sx={{ height: 20, fontSize: 10 }} label={e} />
							))}
							{o.needs_qa === true && (
								<Chip size="small" color="error"
									sx={{ height: 20, fontSize: 10 }} label="needs QA" />
							)}
						</Stack>
						{/* Brand and title read from THIS crop. Only present when
						    the sighting arrived through a merge: it is the text
						    that decided the grouping, and without showing it the
						    operator only sees what the survivor claims. It may
						    legitimately differ — the resource groups sizes, and
						    this is where you see which one each sighting was. */}
						{o.own_text && (
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>
								read here: <Box component="span" sx={{ fontWeight: 600 }}>
									{o.own_text.product}
								</Box>
								{o.own_text.brands?.length ? ` · ${o.own_text.brands.join(', ')}` : ''}
							</Typography>
						)}
						<ProvenanceLine ev={linkageByOcc[o.id_occurrence]} />
						{o.crop_url && (
							<Typography variant="caption" sx={{
								fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.disabled', fontSize: 10,
							}}>
								{o.crop_url}
							</Typography>
						)}
					</Stack>
				</Stack>
			</Paper>
		})}
	</Stack>
}


function formatTs(iso) {
	if (!iso) return ''
	try {
		const d = new Date(iso)
		const time = d.toTimeString().slice(0, 5)
		const date = d.toISOString().slice(0, 10)
		return `${date} ${time}`
	} catch { return iso }
}
